import type {
  ApiResponse,
  CallStats,
  CallRecord,
  CallSummary,
  Extension,
  IVR,
  Queue,
  PhoneDevice,
  IvrDetail,
  IvrMenuOption,
  QueueDetail,
  TrunkInfo,
  ExtensionGroupInfo,
  InboundRoute,
  OutboundRoute,
  OutboundDialPattern,
  OutboundExtListItem,
  ExtensionStatus,
  QueueStatus,
  ActiveCall,
  YeastarContact,
  YeastarPhonebook,
  CompanyContactsApiResponse,
  PhonebooksApiResponse,
  DialCallApiResponse,
  InboundRouteMode,
  MenuOption,
  RouteDestinationInfo,
  UnreturnedMissedCall,
  QueueCallDetail,
  AgentMissedCallActivity,
  AgentLoginActivity,
  AgentPauseActivity,
  FailoverFollowThroughReport,
  FailoverFollowThroughRow,
} from '@/types';
import { callBackend } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { dispositionBucket, recordMatchesExtension } from '@/utils/callRecords';
import { formatDateTimeForApi } from '@/utils/helpers';
import {
  accumulateFailoverOutcome,
  agentsWhoMissedForCall,
  emptyFailoverSummary,
  hitQueueTimeout,
  matchFailoverLeg,
  normalizeQueueStatus,
  type CdrLike,
} from '@/lib/failoverCorrelation';
import {
  enrichUnreturnedRowsWithCdr,
  type CdrEnrichmentLike,
  type ExtensionPresenceHint,
} from '@/lib/unreturnedMissedCdr';
import {
  formatPresenceStatus,
  isExtensionRegistered,
  onlineDeviceChips,
} from '@/lib/yeastar/presence';

export class ApiError extends Error {
  errcode: number;
  endpoint: string;
  status: number;

  constructor(errcode: number, errmsg: string, endpoint: string, status: number) {
    super(`API Error ${errcode} (${endpoint}): ${errmsg}`);
    this.name = 'ApiError';
    this.errcode = errcode;
    this.endpoint = endpoint;
    this.status = status;
  }
}

function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete('access_token');
    return urlObj.toString();
  } catch {
    return url;
  }
}

// Simple cache implementation
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

class ApiCache {
  private cache = new Map<string, CacheEntry<any>>();
  private defaultTTL = 60000; // 1 minute

  get<T>(key: string, ttl: number = this.defaultTTL): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > ttl) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  set<T>(key: string, data: T): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  clear(): void {
    this.cache.clear();
  }
}

const apiCache = new ApiCache();

const CALL_QUERY_TYPES = ['inbound', 'outbound', 'internal'] as const;
type CallQueryType = (typeof CALL_QUERY_TYPES)[number];
export type ApiVersion = 'v1.0' | 'v2.0';

function mapMemberStatusToExtensionState(status?: string): 'ringing' | 'busy' | null {
  const normalized = status?.toLowerCase() || '';
  if (isEndedMemberStatus(normalized)) return null;
  if (normalized.includes('ring') || normalized.includes('alert')) return 'ringing';
  if (
    normalized.includes('answer') ||
    normalized.includes('talk') ||
    normalized.includes('hold')
  ) {
    return 'busy';
  }
  return null;
}

function mapMemberStatusToCallState(
  status?: string
): 'ringing' | 'talking' | 'hold' | null {
  const normalized = status?.toLowerCase() || '';
  if (isEndedMemberStatus(normalized)) return null;
  if (normalized.includes('hold')) return 'hold';
  if (normalized.includes('ring') || normalized.includes('alert')) return 'ringing';
  if (normalized.includes('answer') || normalized.includes('talk')) return 'talking';
  return null;
}

function isEndedMemberStatus(status?: string) {
  const normalized = status?.toLowerCase() || '';
  return (
    normalized.includes('bye') ||
    normalized.includes('hangup') ||
    normalized.includes('hang up') ||
    normalized.includes('terminated') ||
    normalized.includes('end')
  );
}

function callHasEndedMember(members: any[]) {
  return members.some((member: any) => {
    const values = [
      member?.extension?.member_status,
      member?.inbound?.member_status,
      member?.outbound?.member_status,
      member?.internal?.member_status,
    ];
    return values.some((value) => isEndedMemberStatus(value));
  });
}

function isDeviceOnlineStatus(device: unknown): boolean {
  if (!device || typeof device !== 'object') return false;
  const d = device as {
    status?: number;
    status_list?: Array<{ status?: number }>;
  };
  if (d.status === 1) return true;
  return Array.isArray(d.status_list) && d.status_list.some((row) => row?.status === 1);
}

function isExtensionOnline(onlineStatus?: Record<string, unknown>): boolean {
  // Missing online_status: treat as online (legacy behaviour for status widgets).
  if (!onlineStatus || typeof onlineStatus !== 'object') return true;
  return Object.values(onlineStatus).some((device) => isDeviceOnlineStatus(device));
}

function normalizeNumber(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function queueCallStatusPayload(
  result: ApiResponse<any> | null
): Record<string, unknown> {
  const root = objectValue(result);
  const nested = objectValue(root.data);
  const queueKeys = [
    'waiting_list',
    'waiting_call_list',
    'waiting_calls_list',
    'active_list',
    'active_call_list',
    'active_calls_list',
    'ringing_list',
    'ringing_call_list',
    'ringing_calls_list',
    'waiting_calls',
    'active_calls',
    'ringing_calls',
  ];

  return queueKeys.some((key) => key in nested) ? nested : root;
}

function numericCount(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function arrayCount(value: unknown): number {
  return Array.isArray(value) ? value.length : 0;
}

function parseDateTimeToUnixSeconds(value: string): string {
  const raw = String(value || '').trim();
  if (!raw) return raw;
  if (/^\d+$/.test(raw)) return raw;

  const yearFirstMatch = raw.match(
    /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?)?$/i
  );
  if (yearFirstMatch) {
    const [, year, month, day, hour = '0', minute = '0', second = '0', meridiem] =
      yearFirstMatch;
    let hours = Number(hour);
    if (meridiem) {
      const upper = meridiem.toUpperCase();
      if (upper === 'PM' && hours < 12) hours += 12;
      if (upper === 'AM' && hours === 12) hours = 0;
    }
    return String(
      Math.floor(
        new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
          hours,
          Number(minute),
          Number(second)
        ).getTime() / 1000
      )
    );
  }

  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? raw : String(Math.floor(parsed / 1000));
}

function parseCallRecordUnixSeconds(record: CallRecord): number | null {
  const rawTimestamp = Number((record as any).timestamp);
  if (Number.isFinite(rawTimestamp) && rawTimestamp > 0) {
    return rawTimestamp > 1_000_000_000_000
      ? Math.floor(rawTimestamp / 1000)
      : Math.floor(rawTimestamp);
  }

  const rawTime = String(record.time || '').trim();
  if (!rawTime) return null;

  const parsed = Date.parse(rawTime.replace(/-/g, '/'));
  return Number.isNaN(parsed) ? null : Math.floor(parsed / 1000);
}

function isMaxLimitationError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.errcode === 60002 || /MAX LIMITATION EXCEEDED/i.test(error.message))
  );
}

function cdrRecordMatchesFilters(
  record: CallRecord,
  filters?: {
    startTime?: string;
    endTime?: string;
    extNum?: string;
    disposition?: string;
  }
): boolean {
  if (!filters) return true;

  if (filters.startTime || filters.endTime) {
    const timestamp = parseCallRecordUnixSeconds(record);
    if (timestamp !== null) {
      if (
        filters.startTime &&
        timestamp < Number(parseDateTimeToUnixSeconds(filters.startTime))
      ) {
        return false;
      }
      if (
        filters.endTime &&
        timestamp > Number(parseDateTimeToUnixSeconds(filters.endTime))
      ) {
        return false;
      }
    }
  }

  if (filters.extNum && record.call_from !== filters.extNum) return false;
  if (filters.disposition && record.disposition !== filters.disposition) return false;

  return true;
}

async function fetchCDRFromListFallback(
  page: number,
  pageSize: number,
  filters?: {
    startTime?: string;
    endTime?: string;
    extNum?: string;
    disposition?: string;
  }
): Promise<{ data: CallRecord[]; hasMore: boolean }> {
  const targetCount = page * pageSize + 1;
  const listPageSize = Math.min(Math.max(targetCount * 3, 100), 1000);
  const startBoundary = filters?.startTime
    ? Number(parseDateTimeToUnixSeconds(filters.startTime))
    : null;
  const filtered: CallRecord[] = [];
  let listPage = 1;
  let hasMoreSource = true;

  while (filtered.length < targetCount && hasMoreSource && listPage <= 10) {
    const endpoint = `cdr/list?page_size=${listPageSize}&sort_by=time&order_by=desc&page=${listPage}`;
    const result = await apiRequest<CallRecord[]>(endpoint);
    const records = result?.data || [];
    filtered.push(
      ...records.filter((record) => cdrRecordMatchesFilters(record, filters))
    );

    const totalNumber = (result as any)?.total_number;
    hasMoreSource =
      typeof totalNumber === 'number'
        ? listPage * listPageSize < totalNumber
        : records.length >= listPageSize;

    if (
      startBoundary !== null &&
      records.length > 0 &&
      records.every((record) => {
        const timestamp = parseCallRecordUnixSeconds(record);
        return timestamp !== null && timestamp < startBoundary;
      })
    ) {
      hasMoreSource = false;
    }

    listPage += 1;
  }

  const startIndex = (page - 1) * pageSize;
  const pageData = filtered.slice(startIndex, startIndex + pageSize);

  return {
    data: pageData,
    hasMore: filtered.length > startIndex + pageSize || hasMoreSource,
  };
}

function isRateLimitError(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 429 || error.errcode === 429);
}

function pickDestValue(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const value = normalizeNumber(candidate);
    if (value && value.toLowerCase() !== 'disabled') return value;
  }
  // Allow explicit empty; still return first non-null-ish normalized string
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null) {
      return normalizeNumber(candidate);
    }
  }
  return '';
}

function getDefaultDestination(route: Partial<InboundRoute>): RouteDestinationInfo {
  return {
    type: pickDestValue(
      route.def_dest,
      route.default_destination,
      route.destination
    ),
    value: pickDestValue(
      route.def_dest_value,
      route.default_destination_value,
      route.default_desination_value,
      route.destination_value
    ),
    prefix: normalizeNumber(route.def_dest_prefix),
  };
}

function getBusinessHoursDestination(route: Partial<InboundRoute>): RouteDestinationInfo {
  return {
    type: pickDestValue(route.business_hours_destination),
    value: pickDestValue(route.business_hours_destination_value),
    prefix: normalizeNumber(route.business_hours_destination_prefix),
  };
}

function getOutsideBusinessHoursDestination(
  route: Partial<InboundRoute>
): RouteDestinationInfo {
  return {
    type: pickDestValue(route.outside_business_hours_destination),
    value: pickDestValue(route.outside_business_hours_destination_value),
    prefix: normalizeNumber(route.outside_business_hours_destination_prefix),
  };
}

function getHolidaysDestination(route: Partial<InboundRoute>): RouteDestinationInfo {
  return {
    type: pickDestValue(route.holidays_hours_destination),
    value: pickDestValue(route.holidays_hours_destination_value),
    prefix: normalizeNumber(route.holidays_hours_destination_prefix),
  };
}

function normalizeTimeConditionValue(route: Partial<InboundRoute>): string {
  return normalizeNumber(route.time_condition).toLowerCase();
}

function classifyInboundRoute(route: Partial<InboundRoute>): InboundRouteMode {
  const timeCondition = normalizeTimeConditionValue(route);
  const businessDestination = getBusinessHoursDestination(route);
  const outsideDestination = getOutsideBusinessHoursDestination(route);
  const holidaysDestination = getHolidaysDestination(route);
  const hasTimeConditionSignals = Boolean(
    (timeCondition && timeCondition !== 'disabled') ||
      businessDestination.type ||
      businessDestination.value ||
      outsideDestination.type ||
      outsideDestination.value ||
      holidaysDestination.type ||
      holidaysDestination.value
  );

  if (!hasTimeConditionSignals) {
    return 'standard_editable';
  }

  if (timeCondition === 'custom' || timeCondition.includes('period')) {
    return 'time_condition_custom_unsupported';
  }

  if (
    timeCondition === 'global' ||
    timeCondition === 'route_scope' ||
    timeCondition.includes('default') ||
    timeCondition.includes('timezone') ||
    timeCondition.includes('time zone') ||
    timeCondition.includes('business')
  ) {
    return 'time_condition_global_default';
  }

  if (!timeCondition || timeCondition === 'disabled') {
    // Has business/outside/holiday dest fields without a recognised mode
    if (businessDestination.type || outsideDestination.type) {
      return 'time_condition_global_default';
    }
  }

  return 'unknown_capability';
}

function normalizeInboundRoute(route: InboundRoute): InboundRoute {
  return {
    ...route,
    route_mode: classifyInboundRoute(route),
    normalized_default_destination: getDefaultDestination(route),
    normalized_business_hours_destination: getBusinessHoursDestination(route),
    normalized_outside_business_hours_destination:
      getOutsideBusinessHoursDestination(route),
    normalized_holidays_destination: getHolidaysDestination(route),
    one_click_override_eligible: false,
    last_override_attempt: route.last_override_attempt || 'idle',
  };
}

function mapCallType(type: CallQueryType): ActiveCall['call_type'] {
  switch (type) {
    case 'inbound':
      return 'Inbound';
    case 'outbound':
      return 'Outbound';
    default:
      return 'Internal';
  }
}

function mapPresenceStatus(status?: number): string {
  switch (status) {
    case 0:
      return 'Available';
    case 1:
      return 'Do Not Disturb';
    case 2:
      return 'Away';
    case 3:
      return 'Busy';
    case 4:
      return 'Out to Lunch';
    case 5:
      return 'Be Right Back';
    default:
      return status === undefined ? 'Unknown' : `Status ${status}`;
  }
}

// sessionStorage is missing when this module runs on the server (scheduled reports)
const browserSession = typeof sessionStorage === 'undefined' ? null : sessionStorage;

let accessToken: string | null = browserSession?.getItem('yeastar_accessToken') ?? null;
let pbxHost = '';
let proxyUrl = '';

export function setApiConfig(config: {
  pbxHost: string;
  proxyUrl: string;
  accessToken?: string;
}) {
  pbxHost = config.pbxHost;
  proxyUrl = config.proxyUrl;
  if (config.accessToken) {
    accessToken = config.accessToken;
    browserSession?.setItem('yeastar_accessToken', config.accessToken);
  }
}

export function clearAccessToken() {
  accessToken = null;
  browserSession?.removeItem('yeastar_accessToken');
}

export type PbxTransport = (request: {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  apiVersion: ApiVersion;
}) => Promise<ApiResponse<any> | null>;

let pbxTransport: PbxTransport | null = null;

/**
 * On the server (scheduled reports) PBX requests go straight to one customer's PBX instead of
 * through the browser's backend RPC. Pass null to go back to RPC.
 */
export function setPbxTransport(transport: PbxTransport | null) {
  pbxTransport = transport;
}

/** In-flight GET dedupe so dashboard widgets don't stampede the same backend call. */
const inflightBackendGets = new Map<string, Promise<ApiResponse<any> | null>>();

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientBackendTransportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /connection lost|while action was in flight|websocket|network error|failed to fetch|load failed|timed out|timeout|econnreset|socket hang up/i.test(
    message
  );
}

/**
 * Call the PBX through the authenticated local backend with:
 * - GET in-flight dedupe (multiple widgets sharing one round-trip)
 * - automatic retries on transient WebSocket/transport drops
 *
 * - retries for transient HTTP/network failures
 */
async function convexPbxApiRequest<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body: any,
  apiVersion: ApiVersion
): Promise<ApiResponse<T> | null> {
  const isGet = method === 'GET';
  const dedupeKey = isGet
    ? `${apiVersion}|${method}|${endpoint}`
    : null;

  if (dedupeKey) {
    const existing = inflightBackendGets.get(dedupeKey);
    if (existing) {
      return existing as Promise<ApiResponse<T> | null>;
    }
  }

  const run = (async (): Promise<ApiResponse<T> | null> => {
    const maxAttempts = isGet ? 3 : 2;
    let lastError: unknown;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const responseData = (
          pbxTransport
            ? await pbxTransport({ endpoint, method, body: body ?? undefined, apiVersion })
            : await callBackend(backendApi.pbx.apiRequest, {
                endpoint,
                method,
                body: body ?? undefined,
                apiVersion,
              })
        ) as ApiResponse<T>;

        if (responseData?.errcode && responseData.errcode !== 0) {
          throw new ApiError(
            responseData.errcode,
            responseData.errmsg || 'Unknown PBX error',
            endpoint,
            responseData.errcode
          );
        }

        return responseData;
      } catch (error) {
        lastError = error;

        // Don't retry business/API errors from the PBX itself.
        if (error instanceof ApiError) throw error;

        if (
          !isTransientBackendTransportError(error) ||
          attempt >= maxAttempts - 1
        ) {
          throw error;
        }

        const delayMs = 400 * Math.pow(2, attempt) + Math.floor(Math.random() * 200);
        console.warn(
          `PBX backend transport blip on ${endpoint} (attempt ${attempt + 1}/${maxAttempts}); retrying in ${delayMs}ms`,
          error
        );
        await sleep(delayMs);
      }
    }

    throw lastError;
  })();

  if (dedupeKey) {
    inflightBackendGets.set(dedupeKey, run);
    void run.finally(() => {
      if (inflightBackendGets.get(dedupeKey) === run) {
        inflightBackendGets.delete(dedupeKey);
      }
    });
  }

  return run;
}

/**
 * Make API request to Yeastar PBX
 */
export async function apiRequest<T = any>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  body: any = null,
  isRetry = false,
  apiVersion: ApiVersion = 'v1.0'
): Promise<ApiResponse<T> | null> {
  if (!accessToken || !pbxHost || !proxyUrl) {
    return convexPbxApiRequest<T>(endpoint, method, body, apiVersion);
  }

  if (!accessToken && !isRetry) {
    console.error('No access token available');
    return null;
  }

  // Build the target URL for the proxy
  // Remove protocol from pbxHost since proxy will add https://
  const cleanHost = pbxHost.replace(/^https?:\/\//, '');

  const targetUrl = `${cleanHost}/openapi/${apiVersion}/${endpoint.split('?')[0]}`;
  const params = new URLSearchParams(endpoint.split('?')[1] || '');
  if (accessToken) {
    params.append('access_token', accessToken);
  }

  const finalUrl = `${proxyUrl}/api/proxy/${targetUrl}?${params.toString()}`;
  const safeUrl = sanitizeUrl(finalUrl);

  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'User-Agent': 'YeastarDashboardWebApp',
      'X-Requested-With': 'XMLHttpRequest',
    },
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(finalUrl, options);
    const responseText = await response.text();

    let responseData: ApiResponse<T>;
    try {
      responseData = JSON.parse(responseText);
    } catch (e) {
      console.error('Parse error for ' + endpoint + ':', e);
      throw new Error(
        `PBX returned non-JSON response for ${endpoint}. Check proxy configuration.`
      );
    }

    if (!responseData) {
      throw new Error('PBX returned empty data for ' + endpoint);
    }

    // Token expired
    if (responseData.errcode === 10004 && !isRetry) {
      console.log('Token expired, need to re-authenticate');
      clearAccessToken();
      throw new Error(
        `Authentication expired while calling ${endpoint}. Please reconnect.`
      );
    }

    if (!response.ok || (responseData.errcode && responseData.errcode !== 0)) {
      console.error('API request failed', {
        endpoint,
        status: response.status,
        errcode: responseData.errcode,
        errmsg: responseData.errmsg,
        url: safeUrl,
        method,
      });
      throw new ApiError(
        responseData.errcode ?? response.status,
        responseData.errmsg || 'Unknown error',
        endpoint,
        response.status
      );
    }

    return responseData;
  } catch (error) {
    console.error(`Error in apiRequest for ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Test proxy connection
 */
export async function testProxyConnection(proxyUrl: string): Promise<boolean> {
  try {
    console.log('Testing proxy connection to:', proxyUrl);

    const testUrl = `${proxyUrl}/api/health`;
    const response = await fetch(testUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    console.log('Proxy health check status:', response.status);

    if (response.ok) {
      console.log('✅ Proxy is accessible');
      return true;
    } else {
      console.warn('⚠️ Proxy responded but returned status:', response.status);
      return false;
    }
  } catch (error) {
    console.error('❌ Cannot reach proxy:', error);
    return false;
  }
}

/**
 * Get access token from Yeastar PBX
 */
export async function getAccessToken(
  host: string,
  clientId: string,
  clientSecret: string,
  proxyUrlParam: string
): Promise<string> {
  // Remove protocol from host since proxy will add https://
  const cleanHost = host.replace(/^https?:\/\//, '');

  const targetUrl = `${cleanHost}/openapi/v1.0/get_token`;
  const url = `${proxyUrlParam}/api/proxy/${targetUrl}`;

  console.log('Authentication request URL:', url);
  console.log('Target PBX host:', cleanHost);
  console.log('Request payload:', {
    username: clientId,
    password: '***hidden***',
  });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': 'YeastarDashboardWebApp',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        username: clientId,
        password: clientSecret,
      }),
    });

    const responseText = await response.text();
    let data: ApiResponse;

    console.log('Response status:', response.status);
    console.log('Response text:', responseText.substring(0, 500));

    try {
      data = JSON.parse(responseText);
    } catch (e) {
      console.error('Auth parse error:', responseText.substring(0, 200));
      throw new Error(
        'Proxy/PBX returned invalid response. Check proxy configuration and ensure CORS is enabled.'
      );
    }

    if (data.errcode === 0 && data.access_token) {
      browserSession?.setItem('yeastar_accessToken', data.access_token);
      accessToken = data.access_token;
      console.log('Authentication successful');
      return data.access_token;
    } else {
      const errorMsg = `Auth failed: Error ${data.errcode}: ${data.errmsg || 'Unknown error'}`;
      console.error(errorMsg);
      console.error('Common errors:');
      console.error('- Error 10003: Invalid username/password');
      console.error('- Error 10004: Token expired');
      console.error('- Error 10005: IP not whitelisted');
      throw new Error(errorMsg);
    }
  } catch (error: any) {
    console.error('Error getting access token:', error);

    // Provide more specific error messages
    if (error.message && error.message.includes('Failed to fetch')) {
      throw new Error(
        'Cannot connect to proxy server. Please check:\n' +
          '1. Proxy URL is correct and accessible\n' +
          '2. Proxy server is running\n' +
          '3. CORS is enabled on proxy\n' +
          '4. No browser extensions blocking requests\n' +
          `Proxy URL: ${proxyUrlParam}`
      );
    } else if (error.name === 'TypeError') {
      throw new Error(
        'Network error. Check your internet connection and proxy URL.\n' +
          `Attempting to connect to: ${proxyUrlParam}`
      );
    }

    throw error;
  }
}

/**
 * Fetch all extensions
 */
export async function fetchExtensions(useCache = true): Promise<Extension[]> {
  const cacheKey = 'extensions';
  if (useCache) {
    const cached = apiCache.get<Extension[]>(cacheKey);
    if (cached) return cached;
  }

  const result = await apiRequest<any>('extension/list?page_size=1000');
  if (result && result.errcode === 0) {
    const extensions = ((result as any).extension_list || result.data || []) as any[];
    const mapped = extensions.map((ext) => {
      const firstName = normalizeNumber(ext.first_name || ext.firstname);
      const lastName = normalizeNumber(ext.last_name || ext.lastname);
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      return {
        id: normalizeNumber(ext.id || ext.ext_id || ext.extension_id),
        number: normalizeNumber(ext.number || ext.ext_num),
        display_name:
          fullName ||
          ext.caller_id_name ||
          ext.display_name ||
          ext.username ||
          ext.user_name,
        username: ext.username || ext.user_name,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        email: normalizeNumber(ext.email || ext.email_addr) || undefined,
        mobile_number:
          normalizeNumber(ext.mobile_number || ext.mobile || ext.mobile_num) || undefined,
        role_name:
          normalizeNumber(ext.role_name || ext.title || ext.job_title || ext.department) ||
          undefined,
        caller_id_name: ext.caller_id_name,
        online_status: ext.online_status,
        presence_status: ext.presence_status,
      };
    }) as Extension[];
    apiCache.set(cacheKey, mapped);
    return mapped;
  }
  return [];
}

/**
 * Fetch IVRs
 */
export async function fetchIVRs(useCache = true): Promise<IVR[]> {
  const cacheKey = 'ivrs';
  if (useCache) {
    const cached = apiCache.get<IVR[]>(cacheKey);
    if (cached) return cached;
  }

  const result = await apiRequest<any>('ivr/list?page_size=100');
  if (result && result.errcode === 0) {
    const list = ((result as any).ivr_list || result.data || []) as any[];
    const mapped = list.map((ivr) => ({
      id: normalizeNumber(ivr.id || ivr.ivr_id),
      name: normalizeNumber(ivr.name || ivr.ivr_name),
      number: normalizeNumber(ivr.number || ivr.ivr_num) || undefined,
    })) as IVR[];
    apiCache.set(cacheKey, mapped);
    return mapped;
  }
  return [];
}

/**
 * Fetch generic menu options for routing destinations
 */
export async function fetchMenuOptions(
  menu: string,
  useCache = true
): Promise<MenuOption[]> {
  const cacheKey = `menu-options:${menu}`;
  if (useCache) {
    const cached = apiCache.get<MenuOption[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    const result = await apiRequest<MenuOption[]>(
      `system/get_menuoptions?menu=${encodeURIComponent(menu)}`
    );
    if (result && Array.isArray(result.data)) {
      const options = result.data.map((item) => ({
        text: normalizeNumber((item as any).text),
        value: normalizeNumber((item as any).value),
        type: normalizeNumber((item as any).type) || undefined,
      }));
      apiCache.set(cacheKey, options);
      return options;
    }
  } catch (error) {
    console.warn(`Optional menu options for "${menu}" unavailable:`, error);
  }

  return [];
}

/**
 * Fetch outbound routes for inbound-route destination lookup
 */
export async function fetchOutboundRoutes(useCache = true): Promise<MenuOption[]> {
  const cacheKey = 'outbound-routes';
  if (useCache) {
    const cached = apiCache.get<MenuOption[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    const result = await apiRequest<any>('outbound_route/list?page_size=100');
    if (result && result.errcode === 0) {
      const routes = ((result as any).outbound_route_list || result.data || []) as any[];
      const mapped = routes.map((route) => ({
        text: normalizeNumber(
          route.name || route.route_name || `Outbound Route ${route.id}`
        ),
        value: normalizeNumber(route.id || route.route_id),
      }));
      apiCache.set(cacheKey, mapped);
      return mapped;
    }
  } catch (error) {
    console.warn('Optional outbound route list unavailable:', error);
  }

  return [];
}

function normalizeOutboundDialPatterns(route: any): OutboundDialPattern[] {
  if (Array.isArray(route.dial_pattern_list) && route.dial_pattern_list.length) {
    return route.dial_pattern_list.map((item: any) => ({
      id: item.id != null ? Number(item.id) : undefined,
      pattern: normalizeNumber(item.pattern || item.dial_pattern || ''),
      strip:
        item.strip != null && item.strip !== ''
          ? Number(item.strip)
          : undefined,
      prepend: item.prepend != null ? normalizeNumber(item.prepend) : undefined,
    })).filter((p: OutboundDialPattern) => p.pattern);
  }
  if (Array.isArray(route.dial_pattern) && route.dial_pattern.length) {
    return route.dial_pattern
      .map((p: unknown) => ({ pattern: normalizeNumber(p) }))
      .filter((p: OutboundDialPattern) => p.pattern);
  }
  return [];
}

function normalizeOutboundExtList(items: unknown): OutboundExtListItem[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item: any) => {
      const type = normalizeNumber(item.type || item.ext_type || 'extension').toLowerCase();
      return {
        id: item.id ?? item.ext_id ?? item.group_id,
        type: type || 'extension',
        ext_number: normalizeNumber(
          item.ext_number || item.number || item.ext_num || item.extension
        ) || undefined,
        ext_name: normalizeNumber(
          item.ext_name || item.name || item.display_name || item.group_name
        ) || undefined,
        name: normalizeNumber(item.name || item.ext_name) || undefined,
        number: normalizeNumber(item.number || item.ext_number) || undefined,
      } satisfies OutboundExtListItem;
    })
    .filter((item) => item.id != null || item.ext_number || item.ext_name);
}

function normalizeOutboundRoute(route: any): OutboundRoute {
  const trunkList =
    (Array.isArray(route.trunk_list) && route.trunk_list) ||
    (Array.isArray(route.trunk) && route.trunk) ||
    [];
  return {
    id: Number(route.id || route.route_id),
    name: normalizeNumber(route.name || route.route_name) || `Outbound Route ${route.id}`,
    pos: route.pos != null ? Number(route.pos) : undefined,
    outbound_cid: normalizeNumber(route.outbound_cid || route.outb_cid) || undefined,
    dial_pattern_list: normalizeOutboundDialPatterns(route),
    dial_pattern: Array.isArray(route.dial_pattern)
      ? route.dial_pattern.map((p: unknown) => normalizeNumber(p)).filter(Boolean)
      : undefined,
    trunk_list: trunkList.map((t: any) => ({
      id: Number(t.id),
      name: normalizeNumber(t.name) || undefined,
      type: normalizeNumber(t.type) || undefined,
    })),
    ext_list: normalizeOutboundExtList(route.ext_list || route.extension_list),
    pin_protect: normalizeNumber(route.pin_protect) || undefined,
    pin: route.pin != null ? String(route.pin) : undefined,
    pin_list: route.pin_list,
  };
}

/**
 * Full outbound route list (basic fields from list endpoint).
 */
export async function fetchOutboundRouteList(useCache = true): Promise<OutboundRoute[]> {
  const cacheKey = 'outbound-route-list-full';
  if (useCache) {
    const cached = apiCache.get<OutboundRoute[]>(cacheKey);
    if (cached) return cached;
  }

  const result = await apiRequest<any>(
    'outbound_route/list?page_size=100&sort_by=pos&order_by=asc'
  );
  if (result && result.errcode === 0) {
    const routes = ((result as any).outbound_route_list || result.data || []) as any[];
    const mapped = routes.map(normalizeOutboundRoute);
    apiCache.set(cacheKey, mapped);
    return mapped;
  }
  return [];
}

/**
 * Detailed outbound route (includes ext_list, dial patterns, PIN, etc.).
 */
export async function getOutboundRoute(id: number): Promise<OutboundRoute | null> {
  const result = await apiRequest<any>(`outbound_route/get?id=${id}`);
  if (result && result.errcode === 0 && result.data) {
    return normalizeOutboundRoute(result.data);
  }
  // Some firmwares nest under outbound_route
  if (result && result.errcode === 0 && (result as any).outbound_route) {
    return normalizeOutboundRoute((result as any).outbound_route);
  }
  return null;
}

/**
 * Fetch queues
 */
export async function fetchQueues(useCache = true): Promise<Queue[]> {
  const cacheKey = 'queues';
  if (useCache) {
    const cached = apiCache.get<Queue[]>(cacheKey);
    if (cached) return cached;
  }

  const result = await apiRequest<any>('queue/list?page_size=100');
  if (result && result.errcode === 0) {
    const queues = ((result as any).queue_list || result.data || []) as any[];
    const mapped = queues.map((queue) => ({
      id: normalizeNumber(queue.id || queue.queue_id),
      name: queue.name || queue.queue_name,
      number: normalizeNumber(queue.number || queue.queue_num) || undefined,
    })) as Queue[];
    apiCache.set(cacheKey, mapped);
    return mapped;
  }
  return [];
}

/**
 * Fetch autoprovision phone list (MAC / model / assigned extension).
 */
/**
 * Autoprovision phone inventory.
 * Yeastar exposes phones via phone/search (there is no phone/list endpoint).
 */
export async function fetchPhones(useCache = true): Promise<PhoneDevice[]> {
  const cacheKey = 'phones';
  if (useCache) {
    const cached = apiCache.get<PhoneDevice[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    // Prefer the documented search endpoint; fall back to phone/list if a
    // firmware ever exposes it.
    let result = await apiRequest<any>(
      'phone/search?search_value=&page=1&page_size=1000'
    );
    if (!result || result.errcode !== 0) {
      result = await apiRequest<any>('phone/list?page_size=1000');
    }
    if (result && result.errcode === 0) {
      const list = ((result as any).phone_list || result.data || []) as any[];
      const mapped = list
        .map((phone) => {
          const rawAssign = normalizeNumber(
            phone.assigned_ext_num || phone.ext_num || phone.extension_number
          );
          const assigned =
            rawAssign && !/^unassigned$/i.test(rawAssign) ? rawAssign : undefined;
          return {
            id: normalizeNumber(phone.id || phone.phone_id),
            mac: normalizeNumber(phone.mac || phone.mac_address),
            vendor: normalizeNumber(phone.vendor) || undefined,
            model: normalizeNumber(phone.model || phone.phone_model) || undefined,
            template_name: normalizeNumber(phone.template_name) || undefined,
            assigned_ext_num: assigned,
            assigned_ext_status:
              normalizeNumber(phone.assigned_ext_status) || undefined,
          } as PhoneDevice;
        })
        .filter((p) => p.mac || p.id);
      apiCache.set(cacheKey, mapped);
      return mapped;
    }
  } catch (error) {
    console.warn('Phone list unavailable:', error);
  }
  return [];
}

/**
 * Fetch SIP / trunk list for inbound routing reports.
 */
export async function fetchTrunks(useCache = true): Promise<TrunkInfo[]> {
  const cacheKey = 'trunks';
  if (useCache) {
    const cached = apiCache.get<TrunkInfo[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    const result = await apiRequest<any>('trunk/list?page_size=100');
    if (result && result.errcode === 0) {
      const list = ((result as any).trunk_list || result.data || []) as any[];
      const mapped = list.map((trunk) => ({
        id: normalizeNumber(trunk.id || trunk.trunk_id),
        name: normalizeNumber(trunk.name || trunk.trunk_name),
        hostPort: normalizeNumber(trunk.host_port || trunk.hostname) || undefined,
        defOutboundCid:
          normalizeNumber(trunk.def_outbound_cid || trunk.outbound_cid) || undefined,
        status: typeof trunk.status === 'number' ? trunk.status : undefined,
      })) as TrunkInfo[];
      apiCache.set(cacheKey, mapped);
      return mapped;
    }
  } catch (error) {
    console.warn('Trunk list unavailable:', error);
  }
  return [];
}

/**
 * Yeastar IVR OpenAPI uses flat fields press0_dest…press9_dest (not a list).
 * Also accept list-shaped payloads from older/alternate firmware.
 */
function parseIvrMenuOptions(raw: any): IvrMenuOption[] {
  const options: IvrMenuOption[] = [];

  for (let digit = 0; digit <= 9; digit += 1) {
    const destType = normalizeNumber(raw?.[`press${digit}_dest`]);
    const destValue = normalizeNumber(raw?.[`press${digit}_dest_value`]);
    if (!destType && !destValue) continue;
    if (destType.toLowerCase() === 'disabled') continue;
    options.push({
      key: String(digit),
      destType,
      destValue,
    });
  }

  const specials: Array<{ key: string; typeKey: string; valueKey: string }> = [
    { key: '#', typeKey: 'press_hash_dest', valueKey: 'press_hash_dest_value' },
    { key: '*', typeKey: 'press_star_dest', valueKey: 'press_star_dest_value' },
  ];
  for (const special of specials) {
    const destType = normalizeNumber(raw?.[special.typeKey]);
    const destValue = normalizeNumber(raw?.[special.valueKey]);
    if (!destType && !destValue) continue;
    if (destType.toLowerCase() === 'disabled') continue;
    options.push({ key: special.key, destType, destValue });
  }

  if (options.length > 0) return options;

  // Fallback for list-shaped responses (if any firmware uses them).
  const lists = [
    raw?.press_key_list,
    raw?.keypress_list,
    raw?.key_press_list,
    raw?.menu_list,
    raw?.dtmf_list,
  ];
  const source = lists.find((item) => Array.isArray(item) && item.length > 0) || [];
  return (source as any[])
    .map((item) => ({
      key: normalizeNumber(item.key || item.press_key || item.digit || item.dtmf),
      destType: normalizeNumber(
        item.dest || item.dest_type || item.destination || item.action
      ),
      destValue: normalizeNumber(
        item.dest_value || item.destination_value || item.value || item.dest_ext
      ),
      destLabel: normalizeNumber(item.dest_name || item.name || item.label) || undefined,
    }))
    .filter((opt) => opt.key);
}

/**
 * Fetch a single IVR with menu destinations (best-effort across API variants).
 */
/** PBX wall-clock time from system/information, or null when unavailable. */
export async function fetchPbxSystemTime(): Promise<{
  systemTime: string;
  dateFormat?: string;
} | null> {
  try {
    const result = await apiRequest<any>('system/information');
    const data = result?.errcode === 0 ? result.data : null;
    if (!data?.system_time) return null;
    return {
      systemTime: String(data.system_time),
      dateFormat: data.system_date_format ? String(data.system_date_format) : undefined,
    };
  } catch (error) {
    console.warn('PBX system time unavailable:', error);
    return null;
  }
}

/**
 * The first leg of each inbound call in the last `days` days, which is where the
 * inbound route sent it. CDR v1 rows are legs; grouped by uid, earliest wins.
 */
export async function fetchRecentInboundFirstLegs(days = 7, maxPages = 10): Promise<CallRecord[]> {
  const end = Math.floor(Date.now() / 1000);
  const start = end - days * 86400;
  const pageSize = 1000;
  const firstByUid = new Map<string, CallRecord>();
  try {
    for (let page = 1; page <= maxPages; page++) {
      const result = await apiRequest<CallRecord[]>(
        `cdr/search?page=${page}&page_size=${pageSize}&start_time=${start}&end_time=${end}&sort_by=time&order_by=asc`
      );
      const rows = result?.data || [];
      for (const row of rows) {
        if (row.call_type !== 'Inbound') continue;
        if (row.timestamp && row.timestamp < start) continue;
        const uid = row.uid || row.id;
        if (!uid) continue;
        const seen = firstByUid.get(uid);
        if (!seen || (row.timestamp ?? 0) < (seen.timestamp ?? 0)) firstByUid.set(uid, row);
      }
      const total = (result as any)?.total_number;
      if (rows.length < pageSize || (typeof total === 'number' && page * pageSize >= total)) {
        break;
      }
    }
  } catch (error) {
    console.warn('Recent inbound calls unavailable:', error);
  }
  return Array.from(firstByUid.values());
}

export async function fetchIvrDetail(id: string): Promise<IvrDetail | null> {
  try {
    const result = await apiRequest<any>(`ivr/get?id=${encodeURIComponent(id)}`);
    if (!result || result.errcode !== 0) return null;
    const raw = result.data || result;
    return {
      id: normalizeNumber(raw.id || id),
      name: normalizeNumber(raw.name || raw.ivr_name),
      number: normalizeNumber(raw.number || raw.ivr_num) || undefined,
      prompt: normalizeNumber(raw.prompt || raw.prompt_name) || undefined,
      timeoutDestType:
        normalizeNumber(raw.timeout_dest || raw.timeout_destination) || undefined,
      timeoutDestValue:
        normalizeNumber(raw.timeout_dest_value || raw.timeout_destination_value) ||
        undefined,
      menu: parseIvrMenuOptions(raw),
    };
  } catch (error) {
    console.warn(`IVR detail ${id} unavailable:`, error);
    return null;
  }
}

function parseQueueAgents(raw: any): Array<{ number: string; name?: string }> {
  const lists = [
    raw?.static_agent_list,
    raw?.dynamic_agent_list,
    raw?.agent_list,
    raw?.members,
    raw?.member_list,
    raw?.agents,
  ].filter((item) => Array.isArray(item)) as any[][];

  const agents: Array<{ number: string; name?: string }> = [];
  const seen = new Set<string>();

  for (const list of lists) {
    for (const agent of list) {
      // Yeastar queue agents: { text: "Name", text2: "4104", value: "37", type: "extension" }
      const number = normalizeNumber(
        agent.number ||
          agent.ext_num ||
          agent.extension ||
          agent.text2 || // extension number in OpenAPI queue/get
          (agent.type === 'extension' ? undefined : agent.value) ||
          agent.text
      );
      const name =
        normalizeNumber(
          agent.name || agent.caller_id_name || agent.text || agent.username
        ) || undefined;
      if (!number || seen.has(number)) continue;
      // Skip opaque internal IDs mistaken as numbers when text2 missing
      if (/^\d+$/.test(number) && number.length <= 3 && !agent.text2 && agent.value === number) {
        continue;
      }
      seen.add(number);
      agents.push({
        number,
        name: name && name !== number ? name : undefined,
      });
    }
  }
  return agents;
}

function formatRingStrategy(value?: string): string | undefined {
  if (!value) return undefined;
  const map: Record<string, string> = {
    ring_all: 'Ring All',
    least_recent: 'Least Recent',
    fewest_calls: 'Fewest Calls',
    random: 'Random',
    rrmemory: 'Round Robin',
    linear: 'Linear',
  };
  return map[value] || value.replace(/_/g, ' ');
}

/**
 * Fetch queue configuration (agents, ring strategy, failover).
 */
export async function fetchQueueDetail(id: string): Promise<QueueDetail | null> {
  try {
    const result = await apiRequest<any>(`queue/get?id=${encodeURIComponent(id)}`);
    if (!result || result.errcode !== 0) return null;
    const raw = result.data || result;
    const agents = parseQueueAgents(raw);

    return {
      id: normalizeNumber(raw.id || id),
      name: normalizeNumber(raw.name || raw.queue_name),
      number: normalizeNumber(raw.number || raw.queue_num) || undefined,
      ringStrategy: formatRingStrategy(
        normalizeNumber(raw.ring_strategy || raw.strategy) || undefined
      ),
      maxWaitTime: raw.max_wait_time ?? raw.timeout ?? raw.ring_timeout,
      failDestType:
        normalizeNumber(raw.fail_dest || raw.fail_destination || raw.timeout_dest) ||
        undefined,
      failDestValue:
        normalizeNumber(
          raw.fail_dest_value || raw.fail_destination_value || raw.timeout_dest_value
        ) || undefined,
      agents,
    };
  } catch (error) {
    console.warn(`Queue detail ${id} unavailable:`, error);
    return null;
  }
}

/**
 * Extension groups (departments / companies) from menu options.
 * Note: extension_group/list|get is not available on all Yeastar RAS firmwares;
 * menuoptions remains the reliable source of group names/IDs.
 */
export async function fetchExtensionGroups(useCache = true): Promise<ExtensionGroupInfo[]> {
  const cacheKey = 'extension-groups';
  if (useCache) {
    const cached = apiCache.get<ExtensionGroupInfo[]>(cacheKey);
    if (cached) return cached;
  }

  try {
    const options = await fetchMenuOptions('ext_group', useCache);
    const mapped = options
      .map((item) => ({
        id: normalizeNumber(item.value),
        name: normalizeNumber(item.text),
      }))
      .filter((g) => g.id && g.name && !/^all\b/i.test(g.name));

    if (mapped.length === 0) {
      apiCache.set(cacheKey, mapped);
      return mapped;
    }

    const ids = mapped.map((group) => group.id).join(',');
    let details: any[] = [];

    // Cloud/Software editions use extension_group; some Appliance firmware
    // exposes the same resource as extensiongroup.
    for (const resource of ['extension_group', 'extensiongroup']) {
      try {
        const result = await apiRequest<any[]>(
          `${resource}/query?ids=${encodeURIComponent(ids)}`
        );
        const candidates = Array.isArray(result?.data)
          ? result.data
          : Array.isArray((result as any)?.extension_group_list)
            ? (result as any).extension_group_list
            : [];
        if (candidates.length > 0) {
          details = candidates;
          break;
        }
      } catch {
        // Try the alternate endpoint name, then gracefully keep name-only groups.
      }
    }

    const detailsById = new Map(
      details.map((detail) => [normalizeNumber(detail.id || detail.group_id), detail])
    );
    const enriched = mapped.map((group) => {
      const detail = detailsById.get(group.id);
      if (!detail) return group;

      const members = Array.isArray(detail.members)
        ? detail.members
        : Array.isArray(detail.member_list)
          ? detail.member_list
          : [];

      return {
        ...group,
        memberSelect: normalizeNumber(detail.member_select) || undefined,
        memberExtensionIds: members
          .map((member: any) =>
            normalizeNumber(member.ext_id || member.extension_id || member.id)
          )
          .filter(Boolean),
        memberExtensionNumbers: members
          .map((member: any) =>
            normalizeNumber(member.ext_num || member.extension || member.number)
          )
          .filter(Boolean),
        membershipAvailable: true,
      } satisfies ExtensionGroupInfo;
    });

    apiCache.set(cacheKey, enriched);
    return enriched;
  } catch (error) {
    console.warn('Extension groups unavailable:', error);
  }
  return [];
}

/**
 * Fetch call statistics
 */
export async function fetchCallStats(
  extensionIds: string[],
  startTime: string,
  endTime: string
): Promise<CallStats[]> {
  if (extensionIds.length === 0) return [];

  const endpoint = `call_report/list?type=extcallstatistics&start_time=${encodeURIComponent(
    startTime
  )}&end_time=${encodeURIComponent(endTime)}&ext_id_list=${extensionIds.join(',')}`;

  const stats = await fetchCallReportStats(endpoint);
  if (stats.length > 0) return stats;

  return await deriveCallStatsFromCdr(extensionIds, startTime, endTime);
}

/**
 * Fetch call statistics by communication type (Inbound/Outbound)
 * This provides more accurate data matching Yeastar's Extension Call Statistics Report
 */
export async function fetchCallStatsByType(
  extensionIds: string[],
  startTime: string,
  endTime: string,
  communicationType: 'Inbound' | 'Outbound'
): Promise<CallStats[]> {
  if (extensionIds.length === 0) return [];

  const endpoint = `call_report/list?type=extcallstatistics&start_time=${encodeURIComponent(
    startTime
  )}&end_time=${encodeURIComponent(endTime)}&ext_id_list=${extensionIds.join(',')}&communication_type=${communicationType}`;

  const stats = await fetchCallReportStats(endpoint);
  if (stats.length > 0) return stats;

  return await deriveCallStatsFromCdr(
    extensionIds,
    startTime,
    endTime,
    communicationType
  );
}

async function fetchCallReportStats(endpoint: string): Promise<CallStats[]> {
  for (const apiVersion of ['v2.0', 'v1.0'] satisfies ApiVersion[]) {
    try {
      const result = await apiRequest<any>(endpoint, 'GET', null, false, apiVersion);
      if (result && result.errcode === 0) {
        const stats = extractCallStats(result);
        if (stats.length > 0) return stats;
      }
    } catch (error) {
      console.warn(`Call report query failed on ${apiVersion}:`, error);
    }
  }

  return [];
}

function extractCallStats(result: any): CallStats[] {
  const candidates = [
    result?.ext_call_statistics_list,
    result?.data,
    result?.callback_result,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as CallStats[];
    if (typeof candidate === 'string' && candidate.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(candidate);
        if (Array.isArray(parsed)) return parsed as CallStats[];
      } catch {
        // Fall through to the CDR-derived fallback below.
      }
    }
  }

  return [];
}

async function extensionLookup(extensionIds: string[]) {
  const cachedExtensions =
    apiCache.get<Extension[]>('extensions') || (await fetchExtensions(true));
  return extensionIds.map((id) => {
    const extension = cachedExtensions.find(
      (item) => item.id === id || item.number === id
    );
    return {
      id,
      number: extension?.number || id,
      name: extension?.display_name || extension?.username || extension?.number || id,
    };
  });
}

function sortCallRecordsDesc(records: CallRecord[]) {
  return [...records].sort((a, b) => {
    const aTime = Number((a as any).timestamp) || Date.parse(a.time || '');
    const bTime = Number((b as any).timestamp) || Date.parse(b.time || '');
    return bTime - aTime;
  });
}

function callRecordKey(record: CallRecord) {
  return (
    (record as any).new_id ||
    (record as any).id ||
    (record as any).uid ||
    [
      record.time,
      record.call_from,
      record.call_to,
      record.call_type,
      record.disposition,
      record.talk_duration,
    ].join('|')
  );
}

/**
 * Every CDR row in a date range, newest first. Pages until total_number rows have come back (or a
 * page comes back empty or short), whatever page size the PBX really uses. Some firmware ignores
 * order_by, so rows are de-duplicated and clipped to the range here rather than relying on order.
 */
export async function fetchCallsInRange(
  startTime: string,
  endTime: string
): Promise<CallRecord[]> {
  const byKey = new Map<string, CallRecord>();
  const startSeconds = parseDateTimeToUnixSeconds(startTime);
  const endSeconds = parseDateTimeToUnixSeconds(endTime);
  let received = 0;
  let page = 1;
  let hasMore = true;
  const pageSize = 10000;

  while (hasMore && page <= 100) {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    params.append('start_time', startSeconds);
    params.append('end_time', endSeconds);
    params.append('sort_by', 'time');
    params.append('order_by', 'desc');

    const result = await apiRequest<CallRecord[]>(`cdr/search?${params.toString()}`);
    const pageRecords = result?.data || [];
    received += pageRecords.length;
    for (const record of pageRecords) {
      const ts = Number(record.timestamp);
      if (ts > 0 && (ts < Number(startSeconds) || ts > Number(endSeconds))) continue;
      const key =
        record.new_id ||
        record.id ||
        [record.uid, record.timestamp ?? record.time, record.call_from, record.call_to, record.disposition, record.duration].join('|');
      byKey.set(String(key), record);
    }

    const totalNumber = Number((result as any)?.total_number);
    hasMore =
      pageRecords.length > 0 &&
      (Number.isFinite(totalNumber) && totalNumber > 0 ? received < totalNumber : pageRecords.length >= pageSize);
    page += 1;
  }

  return sortCallRecordsDesc(Array.from(byKey.values()));
}

function summaryParties(value: unknown): Array<{ name: string; number?: string; status?: string }> {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return { name: item.trim() };
      const o = objectValue(item);
      return {
        name: normalizeNumber(o.name || o.number),
        number: normalizeNumber(o.number) || undefined,
        status: normalizeNumber(o.status) || undefined,
      };
    })
    .filter((party) => party.name);
}

function summaryNames(value: unknown): string[] {
  return joinNameList(value).split(', ').filter(Boolean);
}

function toCallSummary(raw: Record<string, unknown>): CallSummary {
  const seconds = (value: unknown) => (value === null || value === undefined || value === '' ? undefined : Number(value) || 0);
  return {
    uid: normalizeNumber(raw.uid),
    status: normalizeNumber(raw.last_status).toUpperCase(),
    disconnectedBy: normalizeNumber(raw.disconnected_by) || undefined,
    firstParticipant: normalizeNumber(raw.call_to) || undefined,
    secondParticipant: normalizeNumber(raw.second_participant) || undefined,
    lastParticipant: normalizeNumber(raw.last_participant) || undefined,
    queues: summaryParties(raw.queues),
    ivrs: summaryNames(raw.ivrs),
    ringGroups: summaryParties(raw.ring_groups),
    callFlows: joinNameList(raw.call_flows) || undefined,
    callDuration: seconds(raw.call_duration),
    routingDuration: seconds(raw.routing_duration),
    handlingDuration: seconds(raw.handling_duration),
    sourceTrunks: summaryNames(raw.source_trunks),
    destinationTrunks: summaryNames(raw.destination_trunks),
    dids: summaryNames(raw.dids),
    callerCompany: normalizeNumber(raw.call_from_company) || undefined,
    segments: raw.segments != null ? Number(raw.segments) : undefined,
  };
}

/**
 * CDR v2's one-line summary of a call (how it ended, who hung up, the queues and IVRs it went
 * through), found by uid. Some firmware ignores the v2 time filter, but rows still come newest
 * first, so paging stops once it is past the call. Null when the PBX has no v2 CDR.
 */
export async function fetchCallSummary(uid: string, atMs: number): Promise<CallSummary | null> {
  if (!uid) return null;
  const cacheKey = `call-summary:${uid}`;
  const cached = apiCache.get<CallSummary>(cacheKey, 10 * 60_000);
  if (cached) return cached;

  // A leg can start well after the call itself (after an IVR and a queue)
  const earliestMs = atMs - 3 * 60 * 60_000;
  const pageSize = 200;
  for (let page = 1; page <= 10; page += 1) {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    params.append('time_begin', formatDateTimeForApi(new Date(earliestMs)));
    params.append('time_end', formatDateTimeForApi(new Date(atMs + 60 * 60_000)));
    params.append('sort_by', 'time');
    params.append('order_by', 'desc');
    const result = await apiRequest<Record<string, unknown>[]>(
      `cdr/search?${params.toString()}`,
      'GET',
      null,
      false,
      'v2.0'
    );
    const rows = result?.data || [];
    const match = rows.find((row) => normalizeNumber(row.uid) === uid);
    if (match) {
      const summary = toCallSummary(match);
      apiCache.set(cacheKey, summary);
      return summary;
    }
    const oldest = rows[rows.length - 1];
    const oldestMs = oldest ? Number(oldest.timestamp) * 1000 || Date.parse(String(oldest.time || '')) : 0;
    if (rows.length < pageSize || (oldestMs && oldestMs < earliestMs)) break;
  }
  return null;
}

async function deriveCallStatsFromCdr(
  extensionIds: string[],
  startTime: string,
  endTime: string,
  communicationType?: 'Inbound' | 'Outbound'
): Promise<CallStats[]> {
  const extensions = await extensionLookup(extensionIds);
  const byNumber = new Map(
    extensions.map((extension) => [
      extension.number,
      {
        ext_name: extension.name,
        ext_num: extension.number,
        total_call_count: 0,
        answered_calls: 0,
        no_answer_calls: 0,
        busy_calls: 0,
        failed_calls: 0,
        voicemail_calls: 0,
        total_talking_time: 0,
      } satisfies CallStats,
    ])
  );

  const records = await fetchCallsInRange(startTime, endTime);
  records.forEach((record) => {
    if (communicationType && record.call_type !== communicationType) return;

    extensions.forEach((extension) => {
      if (!recordMatchesExtension(record, extension.number)) return;

      const stats = byNumber.get(extension.number);
      if (!stats) return;

      stats.total_call_count = (stats.total_call_count || 0) + 1;
      stats.total_talking_time =
        (stats.total_talking_time || 0) + (record.talk_duration || 0);

      const bucket = dispositionBucket(record);
      if (bucket === 'answered') stats.answered_calls = (stats.answered_calls || 0) + 1;
      if (bucket === 'busy') stats.busy_calls = (stats.busy_calls || 0) + 1;
      if (bucket === 'failed') stats.failed_calls = (stats.failed_calls || 0) + 1;
      if (bucket === 'noAnswer')
        stats.no_answer_calls = (stats.no_answer_calls || 0) + 1;
    });
  });

  return Array.from(byNumber.values());
}

/**
 * Fetch call detail records (CDR)
 */
export async function fetchCDR(
  page: number = 1,
  pageSize: number = 10,
  filters?: {
    startTime?: string;
    endTime?: string;
    extNum?: string;
    disposition?: string;
  }
): Promise<{ data: CallRecord[]; hasMore: boolean }> {
  const baseEndpoint = `cdr/list?page_size=${pageSize}&sort_by=time&order_by=desc&page=${page}`;
  const hasFilters =
    !!filters &&
    (Boolean(filters.startTime) ||
      Boolean(filters.endTime) ||
      Boolean(filters.extNum) ||
      Boolean(filters.disposition));

  let result: ApiResponse<CallRecord[]> | null = null;

  if (hasFilters && filters) {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    if (filters.startTime)
      params.append('start_time', parseDateTimeToUnixSeconds(filters.startTime));
    if (filters.endTime)
      params.append('end_time', parseDateTimeToUnixSeconds(filters.endTime));
    if (filters.disposition) params.append('status', filters.disposition);
    if (filters.extNum) params.append('call_from', filters.extNum);

    const endpoint = `cdr/search?${params.toString()}`;

    try {
      result = await apiRequest<CallRecord[]>(endpoint);
    } catch (error: any) {
      if (error instanceof ApiError && error.errcode === 40002) {
        console.warn('CDR search parameters rejected by PBX, retrying without filters');
        result = await apiRequest<CallRecord[]>(baseEndpoint);
      } else if (isMaxLimitationError(error)) {
        console.warn('CDR search exceeded PBX limits, retrying with CDR list fallback');
        return fetchCDRFromListFallback(page, pageSize, filters);
      } else {
        throw error;
      }
    }
  } else {
    result = await apiRequest<CallRecord[]>(baseEndpoint);
  }

  if (result && result.errcode === 0 && result.data) {
    const totalNumber = (result as any).total_number;
    const hasMore =
      typeof totalNumber === 'number'
        ? page * pageSize < totalNumber
        : result.data.length >= pageSize;

    return {
      data: result.data,
      hasMore,
    };
  }

  return { data: [], hasMore: false };
}

/**
 * Fetch all CDR records for an extension within a date range
 * Used for detailed call reports
 */
export async function fetchExtensionCDR(
  extNum: string,
  startTime: string,
  endTime: string,
  pageSize: number = 10000
): Promise<CallRecord[]> {
  const recordMap = new Map<string, CallRecord>();
  const trimmedExt = String(extNum || '').trim();
  const normalizeDigits = (value: string) => value.replace(/[^\d]/g, '');
  const matchesExtension = (value: string) => {
    const raw = String(value || '').trim();
    if (!raw) return false;
    if (raw === trimmedExt) return true;
    const normalized = normalizeDigits(raw);
    if (normalized === trimmedExt) return true;
    if (normalizeDigits(trimmedExt) === normalized) return true;
    const normalizedExtNoZeros = normalizeDigits(trimmedExt).replace(/^0+/, '');
    if (normalized && normalized.replace(/^0+/, '') === normalizedExtNoZeros) return true;
    if (
      raw.startsWith(trimmedExt) &&
      raw.length > trimmedExt.length &&
      /\D/.test(raw[trimmedExt.length])
    ) {
      return true;
    }
    return false;
  };

  const recordBelongsToExtension = (record: CallRecord) =>
    matchesExtension(record.call_from) ||
    matchesExtension(record.call_to) ||
    matchesExtension((record as any).call_from_number) ||
    matchesExtension((record as any).call_to_number) ||
    matchesExtension((record as any).call_from_name) ||
    matchesExtension((record as any).call_to_name);

  const addRecords = (records: CallRecord[]) => {
    records.filter(recordBelongsToExtension).forEach((record) => {
      recordMap.set(callRecordKey(record), record);
    });
  };

  const searchCdr = async (filterField?: 'call_from' | 'call_to') => {
    let page = 1;
    let hasMore = true;

    while (hasMore && page <= 100) {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('page_size', String(pageSize));
      params.append('start_time', parseDateTimeToUnixSeconds(startTime));
      params.append('end_time', parseDateTimeToUnixSeconds(endTime));
      params.append('sort_by', 'time');
      params.append('order_by', 'desc');
      if (filterField) params.append(filterField, trimmedExt);

      const endpoint = `cdr/search?${params.toString()}`;
      const result = await apiRequest<CallRecord[]>(endpoint);
      const records = result?.data || [];
      addRecords(records);

      const totalNumber = (result as any)?.total_number;
      hasMore =
        typeof totalNumber === 'number'
          ? page * pageSize < totalNumber
          : records.length >= pageSize;
      page++;
    }
  };

  await Promise.all([
    searchCdr('call_from').catch((error) => {
      console.warn('CDR call_from search failed:', error);
    }),
    searchCdr('call_to').catch((error) => {
      console.warn('CDR call_to search failed:', error);
    }),
  ]);

  if (recordMap.size > 0) {
    return sortCallRecordsDesc(Array.from(recordMap.values()));
  }

  let page = 1;
  let hasMore = true;

  while (hasMore) {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    params.append('start_time', parseDateTimeToUnixSeconds(startTime));
    params.append('end_time', parseDateTimeToUnixSeconds(endTime));
    params.append('sort_by', 'time');
    params.append('order_by', 'desc');

    // Try searching CDR - we'll filter by extension in JS since API filtering can be inconsistent
    const endpoint = `cdr/search?${params.toString()}`;

    try {
      const result = await apiRequest<CallRecord[]>(endpoint);

      if (result && result.errcode === 0 && result.data) {
        addRecords(result.data);

        const totalNumber = (result as any).total_number;
        hasMore =
          typeof totalNumber === 'number'
            ? page * pageSize < totalNumber
            : result.data.length >= pageSize;
        page++;
      } else {
        hasMore = false;
      }
    } catch (error: any) {
      console.error('Error fetching extension CDR:', error);
      // If search fails, try basic list endpoint
      if (page === 1) {
        try {
          const basicEndpoint = `cdr/list?page_size=${pageSize}&sort_by=time&order_by=desc`;
          const basicResult = await apiRequest<CallRecord[]>(basicEndpoint);
          if (basicResult && basicResult.errcode === 0 && basicResult.data) {
            addRecords(basicResult.data);
          }
        } catch (fallbackError) {
          console.error('Fallback CDR fetch also failed:', fallbackError);
        }
      }
      hasMore = false;
    }
  }

  return sortCallRecordsDesc(Array.from(recordMap.values()));
}

/**
 * Fetch inbound routes
 */
export async function fetchInboundRoutes(): Promise<InboundRoute[]> {
  const result = await apiRequest<InboundRoute[]>(
    'inbound_route/list?page_size=100&sort_by=pos&order_by=asc'
  );

  if (result && result.data) {
    return result.data.map((route) => normalizeInboundRoute(route));
  }
  return [];
}

/**
 * Get specific inbound route
 */
export async function getInboundRoute(id: number): Promise<InboundRoute | null> {
  const result = await apiRequest<InboundRoute>(`inbound_route/get?id=${id}`);

  if (result && result.errcode === 0 && result.data) {
    return normalizeInboundRoute(result.data);
  }
  return null;
}

/**
 * Update inbound route
 */
export async function updateInboundRoute(
  route: Partial<InboundRoute> & { id: number }
): Promise<boolean> {
  const result = await apiRequest('inbound_route/update', 'POST', route);
  return result?.errcode === 0;
}

async function queryCallsByType(type: CallQueryType): Promise<any[]> {
  try {
    const result = await apiRequest<any>(`call/query?type=${type}`);
    if (result && result.errcode === 0 && Array.isArray(result.data)) {
      return result.data;
    }
    return [];
  } catch (error: any) {
    if (error instanceof ApiError && error.errcode === 10001) {
      console.warn(`call/query not supported for type "${type}" on this PBX`);
      return [];
    }
    if (isRateLimitError(error)) {
      console.warn(`call/query rate limited for type "${type}"`);
      return [];
    }
    throw error;
  }
}

/**
 * Query extension status (call status)
 */
export async function fetchExtensionStatus(
  extensionIds?: string[]
): Promise<ExtensionStatus[]> {
  const extensions = await fetchExtensions(true);
  const filteredExtensions =
    extensionIds && extensionIds.length > 0
      ? extensions.filter((ext) => extensionIds.includes(ext.id))
      : extensions;

  const extensionStatusMap = new Map<string, 'ringing' | 'busy'>();

  try {
    const callResults = await Promise.all(
      CALL_QUERY_TYPES.map(async (type) => ({
        type,
        data: await queryCallsByType(type),
      }))
    );

    for (const callResult of callResults) {
      for (const call of callResult.data) {
        const members = Array.isArray(call.members) ? call.members : [];
        if (callHasEndedMember(members)) continue;

        for (const member of members) {
          const data = member?.extension || member?.inbound || member?.outbound;
          if (!data) continue;
          const extNum = normalizeNumber(data.number || data.ext_num);
          if (!extNum) continue;
          const status = mapMemberStatusToExtensionState(data.member_status);
          if (!status) continue;

          const current = extensionStatusMap.get(extNum);
          if (current === 'busy') continue;
          if (current === 'ringing' && status === 'ringing') continue;
          extensionStatusMap.set(extNum, status);
        }
      }
    }
  } catch (error) {
    console.warn('Failed to query call status for extensions:', error);
  }

  return filteredExtensions.map((ext) => {
    const extNum = normalizeNumber(ext.number || (ext as any).ext_num);
    const online = isExtensionOnline((ext as any).online_status);
    const callStatus = extensionStatusMap.get(extNum);
    const presenceStatus =
      (ext as any).presence_status?.status ??
      (ext as any).presence_status ??
      (ext as any).presence;
    // P-Series reports presence as a string (`do_not_disturb`); older firmware as a number.
    const presenceLabel =
      typeof presenceStatus === 'string'
        ? formatPresenceStatus(presenceStatus)
        : mapPresenceStatus(typeof presenceStatus === 'number' ? presenceStatus : undefined);

    let status: ExtensionStatus['status'] = 'idle';
    if (!online) {
      status = 'unavailable';
    } else if (callStatus === 'busy') {
      status = 'busy';
    } else if (callStatus === 'ringing') {
      status = 'ringing';
    }

    return {
      ext_id: ext.id,
      ext_num: extNum,
      ext_name: (ext as any).ext_name,
      display_name: ext.display_name || (ext as any).caller_id_name || ext.username,
      status,
      call_status:
        status === 'busy' ? 'talking' : status === 'ringing' ? 'ringing' : 'idle',
      presence_status: typeof presenceStatus === 'number' ? presenceStatus : undefined,
      presence_label: presenceLabel,
    };
  });
}

/**
 * Query queue status
 */
let queueStatusInFlight: Promise<QueueStatus[]> | null = null;

export async function fetchQueueStatus(queueId?: string): Promise<QueueStatus[]> {
  if (queueId) return loadQueueStatus(queueId);
  // Several dashboard widgets poll all queues; share one in-flight request.
  if (!queueStatusInFlight) {
    queueStatusInFlight = loadQueueStatus().finally(() => {
      queueStatusInFlight = null;
    });
  }
  return queueStatusInFlight;
}

/** Registration and presence per extension number, for flagging unreachable queue agents. */
async function fetchAgentReachability(): Promise<
  Map<string, { registered?: boolean; presence?: string }>
> {
  const map = new Map<string, { registered?: boolean; presence?: string }>();
  try {
    const extensions = await fetchExtensions(true);
    for (const ext of extensions) {
      if (!ext.number) continue;
      const onlineStatus = (ext as any).online_status;
      const presenceRaw = (ext as any).presence_status;
      map.set(ext.number, {
        registered:
          onlineStatus && typeof onlineStatus === 'object'
            ? isExtensionRegistered(onlineStatus)
            : undefined,
        presence:
          typeof presenceRaw === 'string'
            ? presenceRaw
            : typeof presenceRaw?.status === 'string'
              ? presenceRaw.status
              : undefined,
      });
    }
  } catch (error) {
    console.warn('Extension reachability for queue agents unavailable:', error);
  }
  return map;
}

async function loadQueueStatus(queueId?: string): Promise<QueueStatus[]> {
  const [queues, reachability] = await Promise.all([
    fetchQueues(true),
    fetchAgentReachability(),
  ]);
  const targetQueues = queueId ? queues.filter((queue) => queue.id === queueId) : queues;

  if (targetQueues.length === 0) return [];

  const statuses = await Promise.all(
    targetQueues.map(async (queue) => {
      const cacheKey = `queue-status:${queue.id}`;

      try {
        const [callStatusResult, agentStatusResult] = await Promise.all([
          apiRequest<any>(`queue/call_status?id=${queue.id}`),
          apiRequest<any>(`queue/agent_status?id=${queue.id}`),
        ]);

        const callStatus = queueCallStatusPayload(callStatusResult);
        const agentStatusList = agentStatusResult?.data || [];

        const waitingCount =
          numericCount(callStatus.waiting_calls ?? callStatus.waiting_count) ??
          arrayCount(callStatus.waiting_list);
        const activeCount =
          (numericCount(callStatus.active_calls ?? callStatus.active_count) ??
            arrayCount(callStatus.active_list)) +
          (numericCount(callStatus.ringing_calls ?? callStatus.ringing_count) ??
            arrayCount(callStatus.ringing_list));

        const agents = (agentStatusList || []).map((agent: any) => {
          const callStatusValue = Number.parseInt(agent.call_status, 10);
          let status: QueueStatus['agents'][number]['status'] = 'unavailable';

          switch (callStatusValue) {
            case 1:
              status = 'idle';
              break;
            case 2:
            case 4:
            case 5:
              status = 'busy';
              break;
            case 3:
              status = 'ringing';
              break;
            default:
              status = 'unavailable';
          }

          const agentNum = normalizeNumber(agent.number || agent.agent_num);
          return {
            ...reachability.get(agentNum),
            agent_id: normalizeNumber(agent.number || agent.agent_id || agent.id),
            agent_num: agentNum,
            agent_name: agent.name || agent.agent_name,
            status,
            paused: Number(agent.is_pause) === 1 || agent.paused === true,
            logged_in: agent.is_login === undefined ? true : Number(agent.is_login) === 1,
            pause_reason: normalizeNumber(agent.pause_reason) || undefined,
            agent_type:
              agent.agent_type === 'static' || agent.agent_type === 'dynamic'
                ? agent.agent_type
                : undefined,
          };
        });

        const status: QueueStatus = {
          queue_id: queue.id,
          queue_name: queue.name,
          queue_number: queue.number,
          waiting_count: waitingCount,
          active_count: activeCount,
          agents,
        };

        apiCache.set(cacheKey, status);
        return status;
      } catch (error) {
        // 60001 DATA NOT FOUND: the queue was deleted since queue/list was read.
        // Skip it rather than failing every other queue on the board.
        if (error instanceof ApiError && error.errcode === 60001) {
          console.warn(`Queue ${queue.id} not found on PBX; skipping`);
          return null;
        }
        if (isRateLimitError(error)) {
          console.warn(`Queue polling rate limited for queue ${queue.id}`);
          const cached = apiCache.get<QueueStatus>(cacheKey, 300000);
          if (cached) return cached;
          return {
            queue_id: queue.id,
            queue_name: queue.name,
            queue_number: queue.number,
            waiting_count: 0,
            active_count: 0,
            agents: [],
          };
        }
        throw error;
      }
    })
  );

  return statuses.filter((status): status is QueueStatus => status !== null);
}

/**
 * Query active calls
 */
export async function fetchActiveCalls(): Promise<ActiveCall[]> {
  const cacheKey = 'active-calls';
  const results = await Promise.all(
    CALL_QUERY_TYPES.map(async (type) => ({
      type,
      data: await queryCallsByType(type),
    }))
  );

  const calls: ActiveCall[] = [];

  for (const result of results) {
    const callType = mapCallType(result.type);
    for (const call of result.data) {
      const members = Array.isArray(call.members) ? call.members : [];
      if (callHasEndedMember(members)) continue;

      let callFrom = '';
      let callTo = '';
      let fallbackChannelId = '';
      let callerChannelId = '';
      let extensionChannelId = '';
      let status: ActiveCall['status'] = 'talking';
      let statusPriority = 0;

      for (const member of members) {
        const data = member?.extension || member?.inbound || member?.outbound;
        if (!data) continue;
        const memberChannelId = data.channel_id || data.channelid || '';

        if (member?.inbound || member?.outbound) {
          callFrom = data.from || callFrom;
          callTo = data.to || callTo;
          callerChannelId = callerChannelId || memberChannelId;
        }

        if (member?.extension) {
          extensionChannelId = extensionChannelId || memberChannelId;
        }

        if (!fallbackChannelId) {
          fallbackChannelId = memberChannelId;
        }

        const memberStatus = mapMemberStatusToCallState(data.member_status);
        if (!memberStatus) continue;

        const priority = memberStatus === 'hold' ? 3 : memberStatus === 'ringing' ? 2 : 1;
        if (priority > statusPriority) {
          statusPriority = priority;
          status = memberStatus;
        }
      }

      if (statusPriority === 0) continue;

      if (!callFrom && members.length > 0) {
        const fallback =
          members[0].extension || members[0].inbound || members[0].outbound;
        callFrom = fallback?.number || fallback?.from || '';
        callTo = fallback?.to || '';
      }

      const callId = normalizeNumber(call.id || call.call_id);
      const channelId = callerChannelId || extensionChannelId || fallbackChannelId;
      calls.push({
        call_id: callId || `${callType}-${channelId || callFrom}-${callTo}`,
        channel_id: channelId,
        call_from: callFrom,
        call_to: callTo,
        status,
        duration: 0,
        call_type: callType,
      });
    }
  }

  apiCache.set(cacheKey, calls);
  return calls;
}

/**
 * Hangup a call
 */
export async function hangupCall(channelId: string): Promise<boolean> {
  const result = await apiRequest('call/hangup', 'POST', {
    channel_id: channelId,
  });
  return result?.errcode === 0;
}

/**
 * Transfer a call (blind transfer)
 */
export async function transferCall(
  channelId: string,
  destination: string,
  transferorExtension: string
): Promise<boolean> {
  const result = await apiRequest('call/transfer', 'POST', {
    type: 'blind',
    channel_id: channelId,
    number: destination,
    dial_permission: transferorExtension,
  });
  return result?.errcode === 0;
}

/**
 * Park a call
 */
export async function parkCall(
  channelId: string,
  parkingSlot?: string
): Promise<boolean> {
  const body: Record<string, string> = {
    channel_id: channelId,
  };

  if (parkingSlot) {
    body.parking_slot = parkingSlot;
  }

  const result = await apiRequest('call/park', 'POST', body);
  return result?.errcode === 0;
}

/**
 * Hold a call
 */
export async function holdCall(channelId: string): Promise<boolean> {
  const result = await apiRequest('call/hold', 'POST', {
    channel_id: channelId,
  });
  return result?.errcode === 0;
}

/**
 * Resume a held call
 */
export async function unholdCall(channelId: string): Promise<boolean> {
  const result = await apiRequest('call/unhold', 'POST', {
    channel_id: channelId,
  });
  return result?.errcode === 0;
}

/**
 * Monitor a call (listen, whisper, barge-in)
 */
export async function monitorCall(
  extensionNum: string,
  targetChannelId: string,
  mode: 'listen' | 'whisper' | 'barge'
): Promise<boolean> {
  const listenType = mode === 'listen' ? 1 : mode === 'whisper' ? 2 : 3;
  const result = await apiRequest('call/listen', 'POST', {
    extension: extensionNum,
    channel_id: targetChannelId,
    listen_type: listenType,
  });
  return result?.errcode === 0;
}

/**
 * Clear API cache
 */
export function clearApiCache(): void {
  apiCache.clear();
  inflightBackendGets.clear();
  queueStatusInFlight = null;
}

let apiCacheScope: string | null = null;

/**
 * Cached PBX data (queue IDs, extensions, …) belongs to one client's PBX.
 * Drop it whenever the active client changes so the next client never
 * queries its PBX with the previous client's IDs.
 */
export function setApiCacheScope(customerId: string): void {
  if (apiCacheScope === customerId) return;
  apiCacheScope = customerId;
  clearApiCache();
}

// ============================================
// CMS API Functions
// ============================================

/**
 * Fetch all company contacts from Yeastar
 */
export async function fetchCompanyContacts(
  page: number = 1,
  pageSize: number = 10000
): Promise<{ data: YeastarContact[]; totalNumber: number; hasMore: boolean }> {
  const endpoint = `company_contact/list?page=${page}&page_size=${pageSize}`;
  const result = (await apiRequest<YeastarContact[]>(
    endpoint
  )) as CompanyContactsApiResponse;

  if (result && result.errcode === 0 && result.data) {
    const totalNumber = result.total_number || result.data.length;
    return {
      data: result.data,
      totalNumber,
      hasMore: page * pageSize < totalNumber,
    };
  }

  return { data: [], totalNumber: 0, hasMore: false };
}

/**
 * Search company contacts
 */
export async function searchCompanyContacts(
  query: string,
  page: number = 1,
  pageSize: number = 100
): Promise<{ data: YeastarContact[]; totalNumber: number }> {
  const endpoint = `company_contact/search?keyword=${encodeURIComponent(query)}&page=${page}&page_size=${pageSize}`;
  const result = (await apiRequest<YeastarContact[]>(
    endpoint
  )) as CompanyContactsApiResponse;

  if (result && result.errcode === 0 && result.data) {
    return {
      data: result.data,
      totalNumber: result.total_number || result.data.length,
    };
  }

  return { data: [], totalNumber: 0 };
}

/**
 * Get a specific company contact by ID
 */
export async function getCompanyContact(id: number): Promise<YeastarContact | null> {
  const result = await apiRequest<YeastarContact>(`company_contact/get?id=${id}`);

  if (result && result.errcode === 0 && result.data) {
    return result.data;
  }

  return null;
}

// Type for Yeastar contact write operations (create/update)
interface YeastarContactWriteData {
  first_name: string;
  last_name?: string;
  company?: string;
  email?: string;
  remark?: string;
  number_list: Array<{ num_type: string; number: string }>;
}

/**
 * Create a new company contact in Yeastar
 */
export async function createCompanyContact(
  contact: YeastarContactWriteData
): Promise<{ success: boolean; id?: number }> {
  const result = await apiRequest<{ id: number }>(
    'company_contact/create',
    'POST',
    contact
  );

  if (result && result.errcode === 0) {
    return { success: true, id: result.data?.id };
  }

  return { success: false };
}

/**
 * Update an existing company contact in Yeastar
 */
export async function updateCompanyContact(
  id: number,
  contact: YeastarContactWriteData
): Promise<boolean> {
  const result = await apiRequest('company_contact/update', 'POST', { id, ...contact });
  return result?.errcode === 0;
}

/**
 * Delete a company contact from Yeastar
 */
export async function deleteCompanyContact(id: number): Promise<boolean> {
  const result = await apiRequest(`company_contact/delete?id=${id}`, 'GET');
  return result?.errcode === 0;
}

/**
 * Fetch all phonebooks from Yeastar
 */
export async function fetchPhonebooks(
  page: number = 1,
  pageSize: number = 100
): Promise<YeastarPhonebook[]> {
  const endpoint = `phonebook/list?page=${page}&page_size=${pageSize}`;
  const result = (await apiRequest<YeastarPhonebook[]>(
    endpoint
  )) as PhonebooksApiResponse;

  if (result && result.errcode === 0 && result.data) {
    return result.data;
  }

  return [];
}

/**
 * Make a call (Click2Call)
 * @param caller - Extension number that will make the call
 * @param callee - Destination number to call
 * @param options - Optional settings
 */
export async function dialCall(
  caller: string,
  callee: string,
  options?: {
    dialPermission?: string;
    autoAnswer?: boolean;
  }
): Promise<{ success: boolean; callId?: string }> {
  const body: Record<string, string> = {
    caller,
    callee,
  };

  if (options?.dialPermission) {
    body.dial_permission = options.dialPermission;
  }

  if (options?.autoAnswer !== undefined) {
    body.auto_answer = options.autoAnswer ? 'yes' : 'no';
  }

  const result = await apiRequest<DialCallApiResponse>('call/dial', 'POST', body);

  if (result && result.errcode === 0) {
    return {
      success: true,
      callId: (result as any).call_id || result.data?.call_id,
    };
  }

  return { success: false };
}

// ---------------------------------------------------------------------------
// Missed-call / queue disposition reports (Yeastar call_report)
// ---------------------------------------------------------------------------

function extractReportArray(result: any, preferredKeys: string[]): any[] {
  if (!result) return [];

  const candidates: unknown[] = [];
  for (const key of preferredKeys) {
    if (result[key] !== undefined) candidates.push(result[key]);
  }
  candidates.push(result.data, result.callback_result);

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (typeof candidate === 'string' && candidate.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(candidate);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        // ignore parse errors
      }
    }
  }

  return [];
}

function normalizeUnreturnedMissedCall(raw: any): UnreturnedMissedCall {
  return {
    id: normalizeNumber(raw.id || raw.cdr_id || raw.uid),
    time: normalizeNumber(raw.time) || '',
    call_from:
      normalizeNumber(raw.call_from) ||
      [raw.srcname, raw.src].filter(Boolean).join(' ').trim() ||
      '',
    call_to:
      normalizeNumber(raw.call_to) ||
      [raw.dstname, raw.dst].filter(Boolean).join(' ').trim() ||
      '',
    ring_duration: Number(raw.ring_duration) || 0,
    call_to_type: normalizeNumber(raw.call_to_type) || '',
    unreturn_status: Number(raw.unreturn_status) || 0,
    return_time: normalizeNumber(raw.return_time) || undefined,
    miss_call_type: normalizeNumber(raw.miss_call_type) || '',
    timestamp: raw.timestamp,
    src: normalizeNumber(raw.src) || undefined,
    srcname: normalizeNumber(raw.srcname) || undefined,
    dst: normalizeNumber(raw.dst) || undefined,
    dstname: normalizeNumber(raw.dstname) || undefined,
    call_type: normalizeNumber(raw.call_type) || undefined,
    return_timestamp: raw.return_timestamp,
  };
}

function joinNameList(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const o = item as Record<string, unknown>;
          return (
            normalizeNumber(o.name || o.number || o.trunk_name || o.did_number || o.text) ||
            ''
          );
        }
        return '';
      })
      .filter(Boolean)
      .join(', ');
  }
  return '';
}

function callRecordToEnrichment(record: CallRecord): CdrEnrichmentLike {
  const r = record as CallRecord & Record<string, unknown>;
  // CDR v2 uses last_status (includes ABANDONED); v1 uses disposition.
  const disposition =
    normalizeNumber(r.last_status || record.disposition || r.disposition) || '';
  const ringRaw =
    r.routing_duration != null
      ? Number(r.routing_duration)
      : record.ring_duration != null
        ? Number(record.ring_duration)
        : r.ring_duration != null
          ? Number(r.ring_duration)
          : undefined;
  const durationRaw =
    r.call_duration != null
      ? Number(r.call_duration)
      : record.duration != null
        ? Number(record.duration)
        : record.call_duration != null
          ? Number(record.call_duration)
          : undefined;
  const talkRaw =
    r.handling_duration != null
      ? Number(r.handling_duration)
      : record.talk_duration != null
        ? Number(record.talk_duration)
        : 0;
  const disconnectedBy = normalizeNumber(r.disconnected_by) || undefined;
  const legacyReason = normalizeNumber(record.reason || r.reason) || undefined;
  const reason =
    legacyReason ||
    (disconnectedBy ? `Disconnected by ${disconnectedBy}` : undefined);
  const srcTrunk =
    normalizeNumber(record.src_trunk || r.src_trunk) ||
    joinNameList(r.source_trunks) ||
    undefined;
  const didNumber =
    normalizeNumber(record.did_number || r.did_number || r.did) ||
    joinNameList(r.dids) ||
    undefined;
  const callFlows = joinNameList(r.call_flows) || undefined;

  return {
    id: normalizeNumber(r.id || r.cdr_id || r.uid || r.new_id) || undefined,
    uid: normalizeNumber(r.uid) || undefined,
    new_id: normalizeNumber(r.new_id) || undefined,
    time: normalizeNumber(record.time) || '',
    timestamp: record.timestamp ?? (r.timestamp as number | undefined),
    call_from: normalizeNumber(record.call_from) || '',
    call_to: normalizeNumber(record.call_to) || '',
    disposition,
    talk_duration: talkRaw,
    ring_duration: ringRaw,
    duration: durationRaw,
    call_duration: durationRaw,
    call_type: normalizeNumber(record.call_type) || undefined,
    reason,
    disconnected_by: disconnectedBy,
    src_trunk: srcTrunk,
    dst_trunk:
      normalizeNumber(record.dst_trunk || r.dst_trunk) ||
      joinNameList(r.destination_trunks) ||
      undefined,
    did_number: didNumber,
    dod_number:
      normalizeNumber(record.dod_number || r.dod_number) ||
      joinNameList(r.outbound_caller_ids) ||
      undefined,
    call_flows: callFlows,
    segments: r.segments != null ? Number(r.segments) : undefined,
  };
}

/**
 * Page CDR search for a time window.
 * Prefer openapi/v2.0 (last_status includes ABANDONED, disconnected_by, routing_duration).
 * Fall back to v1.0 disposition fields.
 */
async function fetchCdrRangeForEnrichment(
  startTime: string,
  endTime: string,
  pageSize = 1000
): Promise<CallRecord[]> {
  const recordMap = new Map<string, CallRecord>();

  const pullPages = async (
    buildParams: (page: number) => URLSearchParams,
    apiVersion: ApiVersion
  ) => {
    let page = 1;
    let hasMore = true;
    while (hasMore && page <= 50) {
      const endpoint = `cdr/search?${buildParams(page).toString()}`;
      let result: ApiResponse<CallRecord[]> | null = null;
      try {
        result = await apiRequest<CallRecord[]>(endpoint, 'GET', null, false, apiVersion);
      } catch (error: any) {
        if (error instanceof ApiError && error.errcode === 40002) {
          console.warn(
            `CDR enrichment search params rejected on ${apiVersion}, stopping`
          );
          break;
        }
        if (isMaxLimitationError(error)) {
          console.warn(`CDR enrichment search hit PBX max limitation on ${apiVersion}`);
          break;
        }
        throw error;
      }
      if (!result || result.errcode !== 0) break;

      const records = result.data || [];
      for (const record of records) {
        recordMap.set(`${apiVersion}|${callRecordKey(record)}`, record);
      }

      const totalNumber = (result as any)?.total_number;
      hasMore =
        typeof totalNumber === 'number'
          ? page * pageSize < totalNumber
          : records.length >= pageSize;
      page += 1;
    }
  };

  // v2: time_begin / time_end as PBX display strings; last_status has ABANDONED
  try {
    await pullPages((page) => {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('page_size', String(pageSize));
      params.append('time_begin', startTime);
      params.append('time_end', endTime);
      params.append('sort_by', 'time');
      params.append('order_by', 'desc');
      return params;
    }, 'v2.0');
  } catch (e) {
    console.warn('CDR v2.0 enrichment fetch failed:', e);
  }

  // v1 fallback (or fill gaps) — disposition never includes ABANDONED
  if (recordMap.size === 0) {
    try {
      await pullPages((page) => {
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('page_size', String(pageSize));
        params.append('start_time', parseDateTimeToUnixSeconds(startTime));
        params.append('end_time', parseDateTimeToUnixSeconds(endTime));
        params.append('sort_by', 'time');
        params.append('order_by', 'desc');
        return params;
      }, 'v1.0');
    } catch (e) {
      console.warn('CDR v1.0 enrichment fetch failed:', e);
    }
  }

  return Array.from(recordMap.values());
}

function mapEnrichedUnreturnedRow(
  row: ReturnType<typeof enrichUnreturnedRowsWithCdr>[number]
): UnreturnedMissedCall {
  const {
    whatHappened,
    matched,
    cdrDisposition,
    cdrReason,
    cdrDisconnectedBy,
    cdrRingSeconds,
    cdrTalkSeconds,
    cdrDurationSeconds,
    cdrCallType,
    srcTrunk,
    didNumber,
    callPath,
    dispositionMismatch,
    zeroRing,
    extRegistered,
    extDevices,
    extPresence,
    ...base
  } = row;
  return {
    ...(base as UnreturnedMissedCall),
    matched,
    cdrDisposition,
    cdrReason,
    cdrDisconnectedBy,
    cdrRingSeconds,
    cdrTalkSeconds,
    cdrDurationSeconds,
    cdrCallType,
    srcTrunk,
    didNumber,
    callPath,
    dispositionMismatch,
    zeroRing,
    extRegistered,
    extDevices,
    extPresence,
    what_happened: whatHappened,
  };
}

async function buildPresenceByExtension(): Promise<Map<string, ExtensionPresenceHint>> {
  const map = new Map<string, ExtensionPresenceHint>();
  try {
    const extensions = await fetchExtensions(true);
    for (const ext of extensions) {
      if (!ext.number) continue;
      const registered = isExtensionRegistered(ext.online_status as any);
      const devices = onlineDeviceChips(ext.online_status as any);
      const presenceRaw = ext.presence_status;
      const presenceStr =
        typeof presenceRaw === 'string'
          ? presenceRaw
          : presenceRaw && typeof presenceRaw === 'object'
            ? String((presenceRaw as any).status ?? '')
            : '';
      map.set(ext.number, {
        number: ext.number,
        registered,
        devices,
        presence: formatPresenceStatus(presenceStr, null),
      });
    }
  } catch (e) {
    console.warn('Extension presence for unreturned report failed:', e);
  }
  return map;
}

async function enrichUnreturnedWithCdr(
  rows: UnreturnedMissedCall[],
  startTime: string,
  endTime: string
): Promise<UnreturnedMissedCall[]> {
  if (rows.length === 0) return rows;
  const presenceByExt = await buildPresenceByExtension();
  try {
    const cdrs = await fetchCdrRangeForEnrichment(startTime, endTime);
    return enrichUnreturnedRowsWithCdr(
      rows,
      cdrs.map(callRecordToEnrichment),
      presenceByExt
    ).map(mapEnrichedUnreturnedRow);
  } catch (e) {
    console.warn('Unreturned missed CDR enrichment failed:', e);
    // Still attach zero-ring explanations + presence without CDR
    return enrichUnreturnedRowsWithCdr(rows, [], presenceByExt).map(
      mapEnrichedUnreturnedRow
    );
  }
}

function formatSecondsShort(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function cleanAgentLabel(name?: string, num?: string): string {
  const raw = [name, num].filter(Boolean).join(' ').trim();
  if (!raw || /^none$/i.test(raw) || raw === 'NONE') return '';
  return raw.replace(/\s+/g, ' ').trim();
}

/**
 * Yeastar "reason" strings are machine-oriented
 * (e.g. "Alice<1001> caller_call_callee Bob<1002> hangup").
 * Soften them for operators.
 */
export function humanizeQueueReason(reason?: string): string {
  if (!reason) return '';
  let text = reason.replace(/\s+/g, ' ').trim();
  text = text
    .replace(/caller_call_callee/gi, '→')
    .replace(/callee_call_caller/gi, '←')
    .replace(/\bhangup\b/gi, 'hung up')
    .replace(/\bno[_ ]?answer\b/gi, 'no answer')
    .replace(/\bbusy\b/gi, 'busy');
  return text;
}

/**
 * Plain-language "what happened" for a queue call.
 * ring_duration is wait/ring time in the queue (often 30s = agent ring timeout).
 */
export function explainQueueCallOutcome(row: QueueCallDetail): string {
  const status = String(row.status || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  const wait = formatSecondsShort(row.ring_duration || 0);
  const queue = row.queue_name || 'the queue';
  const attempts = row.polling_attempts;
  const lastAgent = cleanAgentLabel(row.agent_name, row.agent_num);
  const missedAgents = (row.agents_who_missed || []).filter(Boolean);
  if (status.includes('abandon') || status === 'abandond') {
    let s = `Caller hung up after waiting ${wait} in ${queue}.`;
    if (missedAgents.length > 0) {
      s += ` Phones that rang without answer: ${missedAgents.join(', ')}.`;
    } else if (lastAgent) {
      s += ` Last offered to ${lastAgent}.`;
    } else {
      s += ` They left before anyone answered.`;
    }
    return s;
  }

  if (
    status.includes('miss') ||
    status.includes('noanswer') ||
    status === 'noanswered'
  ) {
    let s = `No agent answered in ${queue} after ${wait}`;
    if (attempts != null && attempts > 0) {
      s += ` (${attempts} ring attempt${attempts === 1 ? '' : 's'})`;
    }
    s += '.';
    if (missedAgents.length > 0) {
      s += ` Agents who were rung and did not answer: ${missedAgents.join(', ')}.`;
    } else if (lastAgent) {
      s += ` Last offered to ${lastAgent} (did not answer).`;
    } else {
      s += ` No agent connected to the call.`;
    }
    return s;
  }

  if (status.includes('answer') && !status.includes('no')) {
    return `Answered by ${lastAgent || 'an agent'} after ${wait} in ${queue}.`;
  }

  const soft = humanizeQueueReason(row.reason);
  return soft || `Outcome: ${row.status || 'unknown'} after ${wait} in ${queue}.`;
}

function normalizeQueueCallDetail(raw: any, fallbackQueue?: Queue): QueueCallDetail {
  const status = String(raw.status || raw.disposition || '').toLowerCase();
  const row: QueueCallDetail = {
    id: normalizeNumber(raw.id || raw.cdr_id || raw.uid),
    time: normalizeNumber(raw.time) || '',
    call_from: normalizeNumber(raw.call_from) || '',
    call_to: normalizeNumber(raw.call_to) || undefined,
    queue_name:
      normalizeNumber(raw.queue_name) ||
      fallbackQueue?.name ||
      normalizeNumber(raw.queue) ||
      '',
    queue_num:
      normalizeNumber(raw.queue_num) || fallbackQueue?.number || undefined,
    agent_name: normalizeNumber(raw.agent_name) || undefined,
    agent_num: normalizeNumber(raw.agent_num) || undefined,
    status,
    ring_duration:
      Number(raw.ring_duration ?? raw.wait_time ?? raw.waiting_time) || 0,
    talk_duration: Number(raw.talk_duration) || 0,
    hold_duration: Number(raw.hold_duration) || 0,
    reason: normalizeNumber(raw.reason) || undefined,
    process_result: normalizeNumber(raw.process_result) || undefined,
    polling_attempts:
      raw.polling_attempts != null ? Number(raw.polling_attempts) : undefined,
    process_ext: normalizeNumber(raw.process_ext) || undefined,
    process_time: normalizeNumber(raw.process_time) || undefined,
    timestamp: raw.timestamp != null ? Number(raw.timestamp) : undefined,
    disposition: normalizeNumber(raw.disposition) || undefined,
    auto_sync_process_result:
      raw.auto_sync_process_result != null
        ? Number(raw.auto_sync_process_result)
        : undefined,
  };
  row.what_happened = explainQueueCallOutcome(row);
  return row;
}


export interface UnreturnedMissedCallFilters {
  startTime: string;
  endTime: string;
  /** Caller number filter */
  callFrom?: string;
  /** Callee number filter */
  callTo?: string;
  /** Extension | Queue | RingGroup */
  callToType?: string;
  /** no_answer | busy | abandoned */
  missCallType?: string;
  /** 1 = Returned, 2 = Not returned */
  unreturnStatus?: 1 | 2;
  /** Exclude calls abandoned within this many seconds */
  abandonTime?: number;
}

/**
 * Unreturned Missed Call Report — matches Yeastar PBX report, then enriches
 * each row with matching CDR disposition/reason (Busy vs No Answer, 0s ring).
 * API: call_report/list?type=unreturnmisscall + cdr/search
 */
export async function fetchUnreturnedMissedCalls(
  filters: UnreturnedMissedCallFilters
): Promise<UnreturnedMissedCall[]> {
  const params = new URLSearchParams();
  params.set('type', 'unreturnmisscall');
  params.set('start_time', filters.startTime);
  params.set('end_time', filters.endTime);
  if (filters.callFrom) params.set('call_from', filters.callFrom);
  if (filters.callTo) params.set('call_to', filters.callTo);
  if (filters.callToType) params.set('call_to_type', filters.callToType);
  if (filters.missCallType) params.set('miss_call_type', filters.missCallType);
  if (filters.unreturnStatus != null) {
    params.set('unreturn_status', String(filters.unreturnStatus));
  }
  if (filters.abandonTime != null && filters.abandonTime > 0) {
    params.set('abandon_time', String(filters.abandonTime));
  }

  const endpoint = `call_report/list?${params.toString()}`;
  let baseRows: UnreturnedMissedCall[] = [];

  for (const apiVersion of ['v2.0', 'v1.0'] satisfies ApiVersion[]) {
    try {
      const result = await apiRequest<any>(endpoint, 'GET', null, false, apiVersion);
      if (result && result.errcode === 0) {
        const rows = extractReportArray(result, [
          'unreturn_miss_call_list',
          'unreturn_missed_call_list',
        ]);
        if (rows.length > 0 || result.errcode === 0) {
          baseRows = rows.map(normalizeUnreturnedMissedCall);
          break;
        }
      }
    } catch (error) {
      console.warn(
        `Unreturned missed call report failed on ${apiVersion}:`,
        error
      );
    }
  }

  return enrichUnreturnedWithCdr(baseRows, filters.startTime, filters.endTime);
}

export interface QueueCallDetailFilters {
  startTime: string;
  endTime: string;
  /** Empty = all queues */
  queueIds?: string[];
  /** Exclude short abandons (seconds) */
  abandonTime?: number;
}

/**
 * Queue call details via Queue Performance detail endpoint.
 * UI pairs these with live queue config (agents, strategy, timeout, failover).
 *
 * API: call_report/detail?type=queueperformance
 */
export async function fetchQueueCallDetails(
  filters: QueueCallDetailFilters
): Promise<QueueCallDetail[]> {
  const queues = await fetchQueues(true);
  if (queues.length === 0) return [];

  const selected =
    filters.queueIds && filters.queueIds.length > 0
      ? queues.filter((q) => filters.queueIds!.includes(q.id) || filters.queueIds!.includes(q.number || ''))
      : queues;

  if (selected.length === 0) return [];

  const collected: QueueCallDetail[] = [];
  const errors: string[] = [];

  // Detail API expects a single queue_num; query each selected queue.
  for (const queue of selected) {
    const params = new URLSearchParams();
    params.set('type', 'queueperformance');
    params.set('start_time', filters.startTime);
    params.set('end_time', filters.endTime);
    params.set('detail_time_begin', filters.startTime);
    params.set('detail_time_end', filters.endTime);
    params.set('queue_id_list', queue.id);
    if (queue.number) params.set('queue_num', queue.number);
    if (filters.abandonTime != null && filters.abandonTime > 0) {
      params.set('abandon_time', String(filters.abandonTime));
    }

    const endpoint = `call_report/detail?${params.toString()}`;
    let gotRows = false;

    for (const apiVersion of ['v2.0', 'v1.0'] satisfies ApiVersion[]) {
      try {
        const result = await apiRequest<any>(
          endpoint,
          'GET',
          null,
          false,
          apiVersion
        );
        if (result && result.errcode === 0) {
          const rows = extractReportArray(result, [
            'queue_performance_detail',
            'queue_performance_activity_detail',
            'inbound_queue_logs_list',
          ]);
          for (const row of rows) {
            collected.push(normalizeQueueCallDetail(row, queue));
          }
          gotRows = true;
          break;
        }
      } catch (error: any) {
        const msg = error?.message || String(error);
        console.warn(
          `Queue detail for ${queue.name || queue.id} failed on ${apiVersion}:`,
          msg
        );
        if (apiVersion === 'v1.0') errors.push(`${queue.name || queue.id}: ${msg}`);
      }
    }

    // Fallback: try inbound queue logs list type if detail returned nothing
    // (some PBX builds expose this via call_report/list with a dataset name).
    if (!gotRows) {
      for (const typeName of ['inboundqueuelogs', 'inbound_queue_logs']) {
        try {
          const listParams = new URLSearchParams();
          listParams.set('type', typeName);
          listParams.set('start_time', filters.startTime);
          listParams.set('end_time', filters.endTime);
          listParams.set('queue_id_list', queue.id);
          if (filters.abandonTime != null && filters.abandonTime > 0) {
            listParams.set('abandon_time', String(filters.abandonTime));
          }
          const listEndpoint = `call_report/list?${listParams.toString()}`;
          for (const apiVersion of ['v2.0', 'v1.0'] satisfies ApiVersion[]) {
            try {
              const result = await apiRequest<any>(
                listEndpoint,
                'GET',
                null,
                false,
                apiVersion
              );
              if (result && result.errcode === 0) {
                const rows = extractReportArray(result, [
                  'inbound_queue_logs_list',
                  'queue_performance_detail',
                ]);
                if (rows.length > 0) {
                  for (const row of rows) {
                    collected.push(normalizeQueueCallDetail(row, queue));
                  }
                  gotRows = true;
                  break;
                }
              }
            } catch {
              // try next version
            }
          }
          if (gotRows) break;
        } catch {
          // try next type name
        }
      }
    }
  }

  // Deduplicate by id
  const byId = new Map<string, QueueCallDetail>();
  for (const row of collected) {
    const key =
      row.id ||
      `${row.time}|${row.call_from}|${row.queue_num || row.queue_name}|${row.status}`;
    if (!byId.has(key)) byId.set(key, row);
  }

  const rows = Array.from(byId.values()).sort((a, b) => {
    const aTs = a.timestamp || Date.parse(String(a.time).replace(/-/g, '/')) || 0;
    const bTs = b.timestamp || Date.parse(String(b.time).replace(/-/g, '/')) || 0;
    return bTs - aTs;
  });

  if (rows.length === 0 && errors.length > 0) {
    throw new Error(
      `Queue missed-call report unavailable: ${errors.slice(0, 2).join('; ')}`
    );
  }

  return rows;
}

// ---------------------------------------------------------------------------
// Agent activity + Failover follow-through
// ---------------------------------------------------------------------------

async function callReportList(
  type: string,
  params: URLSearchParams
): Promise<any[]> {
  params.set('type', type);
  const endpoint = `call_report/list?${params.toString()}`;
  for (const apiVersion of ['v2.0', 'v1.0'] satisfies ApiVersion[]) {
    try {
      const result = await apiRequest<any>(endpoint, 'GET', null, false, apiVersion);
      if (result && result.errcode === 0) {
        return extractReportArray(result, [
          'queue_agent_miss_calls_list',
          'queue_agent_login_time_list',
          'queue_agent_pause_time_list',
          'queue_performance_detail',
          'data',
        ]);
      }
    } catch (error) {
      console.warn(`call_report/list type=${type} failed on ${apiVersion}:`, error);
    }
  }
  return [];
}

function extensionDisplayNameLocal(ext: Extension): string {
  return (
    ext.display_name ||
    [ext.first_name, ext.last_name].filter(Boolean).join(' ').trim() ||
    ext.username ||
    ext.caller_id_name ||
    ''
  );
}

/** A call queue's setup by its number, for the call detail view: agents, ring strategy, failover. */
export async function fetchQueueSetup(queueNumber: string): Promise<{
  detail: QueueDetail;
  agents: string[];
  failover: string;
} | null> {
  const queue = (await fetchQueues()).find((item) => item.number === queueNumber);
  if (!queue) return null;
  const [detail, extensions] = await Promise.all([fetchQueueDetail(queue.id), fetchExtensions()]);
  if (!detail) return null;
  const extById = new Map(extensions.map((ext) => [String(ext.id), ext]));
  const extByNumber = new Map(extensions.map((ext) => [String(ext.number), ext]));
  const agents = detail.agents.map((agent) => {
    const ext = extByNumber.get(agent.number);
    const name = agent.name || (ext ? extensionDisplayNameLocal(ext) : '');
    return name ? `${name} (${agent.number})` : agent.number;
  });
  return { detail, agents, failover: resolveFailoverExtNumber(detail, extById, extByNumber).label };
}

function resolveFailoverExtNumber(
  detail: QueueDetail | null | undefined,
  extById: Map<string, Extension>,
  extByNumber: Map<string, Extension>
): { number?: string; label: string } {
  if (!detail) return { label: '—' };
  const type = (detail.failDestType || '').trim().toLowerCase();
  const value = (detail.failDestValue || '').trim();
  if (detail.failDestLabel) {
    // Prefer resolved number when type is extension
    if (type === 'extension' || type === 'ext_vm' || type.startsWith('ext')) {
      const ext = extById.get(value) || extByNumber.get(value);
      if (ext) {
        const name = extensionDisplayNameLocal(ext);
        const base = name ? `${name} (${ext.number})` : ext.number;
        return {
          number: ext.number,
          label: type === 'ext_vm' ? `Voicemail ${base}` : base,
        };
      }
    }
    return { label: detail.failDestLabel };
  }
  if (!type && !value) return { label: '—' };
  if (type === 'end_call' || type === 'hang_up' || type.includes('hangup')) {
    return { label: 'Hang up' };
  }
  if (type === 'extension' || type === 'ext_vm' || type.startsWith('ext')) {
    const ext = extById.get(value) || extByNumber.get(value);
    if (ext) {
      const name = extensionDisplayNameLocal(ext);
      const base = name ? `${name} (${ext.number})` : ext.number;
      return {
        number: ext.number,
        label: type === 'ext_vm' ? `Voicemail ${base}` : base,
      };
    }
    // Value might already be the dial number
    if (/^\d{3,6}$/.test(value)) return { number: value, label: `Extension ${value}` };
    return { label: value ? `Extension ID ${value}` : 'Extension' };
  }
  if (type === 'queue' || type.includes('queue')) {
    return { label: value ? `Queue ${value}` : 'Queue' };
  }
  if (type === 'external_num') return { label: value ? `External ${value}` : 'External' };
  // Try resolve unknown type as extension id
  const ext = extById.get(value) || extByNumber.get(value);
  if (ext) {
    const name = extensionDisplayNameLocal(ext);
    return {
      number: ext.number,
      label: name ? `${name} (${ext.number})` : ext.number,
    };
  }
  if (type && value) return { label: `${type.replace(/_/g, ' ')}: ${value}` };
  return { label: type || value || '—' };
}

/**
 * Agent Missed Call Activity — who was offered a queue call and didn't answer.
 * API: call_report/list?type=queueagentmisscalls (requires queue_id).
 */
export async function fetchAgentMissedCallActivity(filters: {
  startTime: string;
  endTime: string;
  queueId: string;
  abandonTime?: number;
}): Promise<AgentMissedCallActivity[]> {
  const params = new URLSearchParams();
  params.set('start_time', filters.startTime);
  params.set('end_time', filters.endTime);
  params.set('queue_id', filters.queueId);
  if (filters.abandonTime != null && filters.abandonTime > 0) {
    params.set('abandon_time', String(filters.abandonTime));
  }
  const rows = await callReportList('queueagentmisscalls', params);
  return rows.map((raw: any) => ({
    time: normalizeNumber(raw.time) || '',
    agent_name: normalizeNumber(raw.agent_name) || undefined,
    agent_num: normalizeNumber(raw.agent_num || raw.agent_number) || '',
    src_name: normalizeNumber(raw.src_name) || undefined,
    src_num: normalizeNumber(raw.src_num || raw.src) || undefined,
    call_from: normalizeNumber(raw.call_from || raw.src_num || raw.src) || undefined,
    total_wait_time:
      raw.total_wait_time != null ? Number(raw.total_wait_time) : undefined,
    queue_status: normalizeNumber(raw.queue_status) || undefined,
    polling_attempts:
      raw.polling_attempts != null ? Number(raw.polling_attempts) : undefined,
    queue: normalizeNumber(raw.queue) || undefined,
    queue_id: filters.queueId,
    timestamp: raw.timestamp != null ? Number(raw.timestamp) : undefined,
  }));
}

/**
 * Agent Login Activity.
 * API: call_report/list?type=queueagentlogintime (requires queue_id).
 */
export async function fetchAgentLoginActivity(filters: {
  startTime: string;
  endTime: string;
  queueId: string;
}): Promise<AgentLoginActivity[]> {
  const params = new URLSearchParams();
  params.set('start_time', filters.startTime);
  params.set('end_time', filters.endTime);
  params.set('queue_id', filters.queueId);
  const rows = await callReportList('queueagentlogintime', params);
  return rows.map((raw: any) => ({
    agent_name: normalizeNumber(raw.agent_name) || undefined,
    agent_number: normalizeNumber(raw.agent_number || raw.agent_num) || '',
    login: normalizeNumber(raw.login || raw.login_time || raw.time) || '',
    logout: normalizeNumber(raw.logout || raw.logout_time) || undefined,
    total_login_time:
      raw.total_login_time != null
        ? Number(raw.total_login_time)
        : raw.login_time != null
          ? Number(raw.login_time)
          : undefined,
    queue: normalizeNumber(raw.queue) || undefined,
    queue_id: filters.queueId,
  }));
}

/**
 * Agent Pause Activity.
 * API: call_report/list?type=queueagentpausetime (requires queue_id).
 */
export async function fetchAgentPauseActivity(filters: {
  startTime: string;
  endTime: string;
  queueId: string;
}): Promise<AgentPauseActivity[]> {
  const params = new URLSearchParams();
  params.set('start_time', filters.startTime);
  params.set('end_time', filters.endTime);
  params.set('queue_id', filters.queueId);
  const rows = await callReportList('queueagentpausetime', params);
  return rows.map((raw: any) => ({
    agent_name: normalizeNumber(raw.agent_name) || undefined,
    agent_number: normalizeNumber(raw.agent_number || raw.agent_num) || '',
    pause: normalizeNumber(raw.pause || raw.pause_time || raw.time) || '',
    unpause: normalizeNumber(raw.unpause || raw.unpause_time) || undefined,
    total_pause_time:
      raw.total_pause_time != null ? Number(raw.total_pause_time) : undefined,
    total_pause: raw.total_pause != null ? Number(raw.total_pause) : undefined,
    reason: normalizeNumber(raw.reason) || undefined,
    queue: normalizeNumber(raw.queue) || undefined,
    queue_id: filters.queueId,
  }));
}

/**
 * Enrich queue call rows with agents_who_missed from Agent Missed Call Activity.
 */
export async function enrichQueueCallsWithAgentMisses(
  rows: QueueCallDetail[],
  queueIdByNumOrName: Map<string, string>,
  startTime: string,
  endTime: string
): Promise<QueueCallDetail[]> {
  const queueIds = new Set<string>();
  for (const row of rows) {
    const key = row.queue_num || row.queue_name;
    const id = queueIdByNumOrName.get(key) || queueIdByNumOrName.get((key || '').toLowerCase());
    if (id) queueIds.add(id);
  }
  const missesByQueue = new Map<string, AgentMissedCallActivity[]>();
  await Promise.all(
    [...queueIds].map(async (qid) => {
      try {
        const misses = await fetchAgentMissedCallActivity({
          startTime,
          endTime,
          queueId: qid,
          abandonTime: 3,
        });
        missesByQueue.set(qid, misses);
      } catch (e) {
        console.warn(`Agent miss activity for queue ${qid} failed:`, e);
        missesByQueue.set(qid, []);
      }
    })
  );

  return rows.map((row) => {
    const key = row.queue_num || row.queue_name;
    const qid =
      queueIdByNumOrName.get(key || '') ||
      queueIdByNumOrName.get((key || '').toLowerCase());
    const misses = (qid && missesByQueue.get(qid)) || [];
    const agents = agentsWhoMissedForCall(row, misses);
    if (agents.length === 0) return row;
    const enriched: QueueCallDetail = {
      ...row,
      agents_who_missed: agents,
    };
    enriched.what_happened = explainQueueCallOutcome(enriched);
    return enriched;
  });
}

export interface FailoverFollowThroughFilters {
  startTime: string;
  endTime: string;
  /** Empty = all queues that have a failover destination */
  queueIds?: string[];
  abandonTime?: number;
}

/**
 * Build Failover Follow-through report:
 * queue misses/timeouts + correlated failover extension CDR + agent miss/login/pause.
 */
export async function fetchFailoverFollowThrough(
  filters: FailoverFollowThroughFilters
): Promise<FailoverFollowThroughReport> {
  const [queues, extensions] = await Promise.all([
    fetchQueues(true),
    fetchExtensions(true),
  ]);

  const selected =
    filters.queueIds && filters.queueIds.length > 0
      ? queues.filter(
          (q) =>
            filters.queueIds!.includes(q.id) ||
            filters.queueIds!.includes(q.number || '')
        )
      : queues;

  const extById = new Map(extensions.map((e) => [e.id, e]));
  const extByNumber = new Map(extensions.map((e) => [e.number, e]));

  const details = new Map<string, QueueDetail>();
  await Promise.all(
    selected.map(async (q) => {
      const d = await fetchQueueDetail(q.id);
      if (d) {
        const resolved = resolveFailoverExtNumber(d, extById, extByNumber);
        details.set(q.id, {
          ...d,
          failDestLabel: resolved.label !== '—' ? resolved.label : d.failDestLabel,
        });
      }
    })
  );

  const queueCallRows = await fetchQueueCallDetails({
    startTime: filters.startTime,
    endTime: filters.endTime,
    queueIds: selected.map((q) => q.id),
    abandonTime: filters.abandonTime ?? 3,
  });

  // Agent activity per queue (best-effort)
  const allMisses: AgentMissedCallActivity[] = [];
  const allLogins: AgentLoginActivity[] = [];
  const allPauses: AgentPauseActivity[] = [];
  const missesByQueue = new Map<string, AgentMissedCallActivity[]>();

  await Promise.all(
    selected.map(async (q) => {
      try {
        const [misses, logins, pauses] = await Promise.all([
          fetchAgentMissedCallActivity({
            startTime: filters.startTime,
            endTime: filters.endTime,
            queueId: q.id,
            abandonTime: filters.abandonTime ?? 3,
          }),
          fetchAgentLoginActivity({
            startTime: filters.startTime,
            endTime: filters.endTime,
            queueId: q.id,
          }),
          fetchAgentPauseActivity({
            startTime: filters.startTime,
            endTime: filters.endTime,
            queueId: q.id,
          }),
        ]);
        missesByQueue.set(q.id, misses);
        allMisses.push(...misses);
        allLogins.push(...logins.map((r) => ({ ...r, queue: r.queue || q.name })));
        allPauses.push(...pauses.map((r) => ({ ...r, queue: r.queue || q.name })));
      } catch (e) {
        console.warn(`Agent activity for ${q.name} failed:`, e);
        missesByQueue.set(q.id, []);
      }
    })
  );

  // Collect unique failover extension numbers for CDR pulls
  const failoverExts = new Set<string>();
  for (const d of details.values()) {
    const resolved = resolveFailoverExtNumber(d, extById, extByNumber);
    if (resolved.number) failoverExts.add(resolved.number);
  }

  const cdrByExt = new Map<string, CdrLike[]>();
  await Promise.all(
    [...failoverExts].map(async (extNum) => {
      try {
        const records = await fetchExtensionCDR(
          extNum,
          filters.startTime,
          filters.endTime
        );
        cdrByExt.set(
          extNum,
          records.map((r) => ({
            time: r.time,
            timestamp: r.timestamp,
            call_from: r.call_from,
            call_to: r.call_to,
            disposition: r.disposition,
            talk_duration: r.talk_duration || 0,
            ring_duration: (r as any).ring_duration,
            call_type: r.call_type,
          }))
        );
      } catch (e) {
        console.warn(`Failover CDR for ${extNum} failed:`, e);
        cdrByExt.set(extNum, []);
      }
    })
  );

  // Index queue id by name/number
  const queueIdByKey = new Map<string, string>();
  for (const q of selected) {
    queueIdByKey.set(q.id, q.id);
    if (q.number) queueIdByKey.set(q.number, q.id);
    if (q.name) queueIdByKey.set(q.name.toLowerCase().trim(), q.id);
  }

  const summary = emptyFailoverSummary();
  const followRows: FailoverFollowThroughRow[] = [];

  for (const row of queueCallRows) {
    const st = normalizeQueueStatus(row.status);
    if (st !== 'missed' && st !== 'abandoned' && st !== 'busy') continue;

    const qid =
      queueIdByKey.get(row.queue_num || '') ||
      queueIdByKey.get((row.queue_name || '').toLowerCase().trim()) ||
      selected.find(
        (q) =>
          q.number === row.queue_num ||
          q.name.toLowerCase() === (row.queue_name || '').toLowerCase()
      )?.id;

    const detail = qid ? details.get(qid) : undefined;
    const resolved = resolveFailoverExtNumber(detail, extById, extByNumber);
    const maxWait = detail?.maxWaitTime != null ? Number(detail.maxWaitTime) : undefined;
    const wait = row.ring_duration || 0;
    const timedOut = hitQueueTimeout(wait, maxWait);
    const abandonedBeforeTimeout = st === 'abandoned' && !timedOut;

    const agentsMissed = agentsWhoMissedForCall(
      row,
      (qid && missesByQueue.get(qid)) || []
    );
    const agentsInQueue = (detail?.agents || []).map((a) =>
      a.name ? `${a.name} (${a.number})` : a.number
    );

    const cdrs = resolved.number ? cdrByExt.get(resolved.number) || [] : [];
    const match = matchFailoverLeg(row, cdrs, {
      failoverExtNumber: resolved.number,
      failoverLabel: resolved.label,
      maxWaitSeconds: maxWait,
    });

    accumulateFailoverOutcome(summary, match, timedOut, abandonedBeforeTimeout);

    const whatParts = [
      explainQueueCallOutcome({ ...row, agents_who_missed: agentsMissed }),
    ];
    if (timedOut && resolved.label !== '—') {
      whatParts.push(`Queue max wait reached; failover is set to: ${resolved.label}.`);
    }
    whatParts.push(match.summary);

    followRows.push({
      id:
        row.id ||
        `${row.time}|${row.call_from}|${row.queue_num || row.queue_name}|${row.status}`,
      time: row.time,
      call_from: row.call_from,
      queue_name: row.queue_name || detail?.name || '',
      queue_num: row.queue_num || detail?.number,
      queue_id: qid,
      queue_status: row.status,
      wait_seconds: wait,
      max_wait_seconds: maxWait,
      timed_out: timedOut,
      ring_strategy: detail?.ringStrategy,
      agents_in_queue: agentsInQueue,
      agents_who_missed: agentsMissed,
      failover_label: resolved.label,
      failover_ext_number: resolved.number,
      failover_outcome: match.outcome,
      failover_summary: match.summary,
      failover_matched: match.matched,
      failover_cdr_time: match.matchedCdrTime,
      failover_disposition: match.matchedDisposition,
      failover_ring_seconds: match.ringSeconds,
      failover_talk_seconds: match.talkSeconds,
      busy_during_failover: match.busyDuringFailover,
      what_happened: whatParts.filter(Boolean).join(' '),
    });
  }

  followRows.sort((a, b) => {
    const at = Date.parse(String(a.time).replace(/-/g, '/')) || 0;
    const bt = Date.parse(String(b.time).replace(/-/g, '/')) || 0;
    return bt - at;
  });

  return {
    rows: followRows,
    summary,
    agentMisses: allMisses,
    agentLogins: allLogins,
    agentPauses: allPauses,
    queues: selected.map((q) => {
      const d = details.get(q.id);
      const resolved = resolveFailoverExtNumber(d, extById, extByNumber);
      return {
        id: q.id,
        name: q.name,
        number: q.number,
        failoverLabel: resolved.label,
      };
    }),
  };
}
